import { HttpClient, HttpEventType } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, NgZone, OnDestroy, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AddTagFn } from '@ng-select/ng-select/lib/ng-select.component';
import { catchError, debounceTime, distinctUntilChanged, forkJoin, of, Subject, takeUntil } from 'rxjs';
import { CompaniesService } from 'src/app/AdvERP/companies/companies.service';
import { IndividualService } from 'src/app/AdvERP/individual/individual.service';
import { IndividualRelationService } from 'src/app/AdvERP/individual/individualRelation.service';
import { CompanyRelationService } from 'src/app/Common/CompanyRelation/company-relation.service';
import { DocMasterCreateRequestDTO, DocMasterDTO } from 'src/app/Common/DocMasterService/docMaster.interface';
import { DocMasterService } from 'src/app/Common/DocMasterService/docMaster.service';
import { DocumentMasterService } from 'src/app/Common/DocumentMasterService/document-master.service';
import { DocumentMasterDTO } from 'src/app/Common/DocumentMasterService/documentmaster.interface';
import { GenericFormUICDTO, GenericFormUICReadByTenantIdRequestDTO } from 'src/app/Common/UICServices/FormUIC/form-uic.interface';
import { GenericFormUICService } from 'src/app/Common/UICServices/FormUIC/form-uic.service';
import { UICMasterType } from 'src/app/Common/UICServices/uic.enum';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { ItemMasterService } from 'src/app/ERP/Products/item-master.service';
import { ItemMasterComponent } from 'src/app/ERP/Products/item-master/item-master.component';
import { DocumentMaster_Upload, PrintMaster_DynamicSQLExecutor } from 'src/app/GlobalVariables';
import { DestinationMasterService } from 'src/app/Logistics/destination-master/destination-master.service';
import { environment } from 'src/environments/environment';
import { AddressDetailService } from '../address-detail/address-detail.service';
import { ContactDetailCreateRequestDTO, ContactDetailDTO } from '../contact-detail/contact-detail.interface';
import { ContactDetailService } from '../contact-detail/contact-detail.service';
import { ensureNumber, formatDateForInput } from '../customFunctions/CustomFunctions';
import { ImageCompressionService } from '../image-compression/image-compression.service';
import { UicDiscountComponent } from '../uic-discount/uic-discount.component';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyMasterCreateRequestDTO } from 'src/app/AdvERP/companies/companies.interface';
import { IndividualMasterCreateRequestDTO } from 'src/app/AdvERP/individual/individual.interface';
import { IndividualRelationCreateRequestDTO } from 'src/app/AdvERP/individual/individualRelation.interface';
import { CompanyRelationCreateRequestDTO } from 'src/app/Common/CompanyRelation/company-relation.interface';
import { ItemMasterUpdateRequestDTO, ItemMasterCreateRequestDTO } from 'src/app/ERP/Products/item-master/item-master.interface';
import { DestinationMasterCreateRequestDTO } from 'src/app/Logistics/destination-master/destination-master.interface';
import { AddressDetailCreateRequestDTO, AddressDetailDTO } from '../address-detail/address-detail.interface';

// ✅ NEW: Refactored UIC Form Services
import { FormBuilderService } from './core/services/form-builder.service';
import { DependentFieldService } from './core/services/dependent-field.service';
import { DataFetchService } from './core/services/data-fetch.service';
import { ValidationService as UICValidationService } from './core/services/validation.service';
import { FormField, DataFetchContext } from './core/models';
import { FORM_CONFIG } from './core/constants';
import { formatDate } from '@angular/common';

@Component({
  selector: 'app-uic-form',
  templateUrl: './uic-form.component.html',
  styleUrls: ['./uic-form.component.css']
})
export class UicFormComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  private dependencyExecutionTracker = new Set<string>();
  private jsExecutorWired = new Set<string>();
  private isEditMode = false;

  // Tells ReadyUICForm() up front that a patchWithDependencies() call is coming for this
  // instance, so jsexecutor auto-run (setupJSExecutorListener) can correctly skip itself
  // from tick zero. Without this, isEditMode only flips true once patchWithDependencies
  // actually runs - which the parent triggers off the formReady output, i.e. strictly
  // AFTER ReadyUICForm() already kicked off this field's own eager dbquery/api loads. If
  // that load resolves before the parent gets around to calling patchWithDependencies (a
  // real race - the parent's own inter-call delay is a fixed setTimeout, the network call
  // is not), the jsexecutor fires early with defaulted/current-user data and briefly
  // clobbers a field that direct row-data patching would otherwise have set correctly.
  @Input() isEditForm: boolean = false;

  // ✅ Feature Flag: Set to true to use refactored services
  @Input() useRefactoredImplementation: boolean = false;
  @Input()
  createCompanyRelation: boolean = true
  @Input({ required: true }) gFormUIC: GenericFormUICDTO[] = [];
  @Input() gFormGroup: FormGroup = new FormGroup({});
  @Input() showModalHeader: boolean = true;
  @Input() showFooter: boolean = true;
  @Input() modalHeader: string = 'Create';
  @Input() submitButtonLabel: string = 'Submit';
  // Optional secondary submit action (e.g. "Next") shown alongside the primary Submit
  // button. Null/unset (the default) means no second button renders - every other
  // masterType using this shared form is unaffected unless it opts in.
  @Input() secondaryActionButtonLabel: string | null = null;
  @Output() formReady: EventEmitter<boolean> = new EventEmitter<boolean>(false)
  @Output() onSubmitEvent: EventEmitter<boolean> = new EventEmitter<boolean>(false)
  @Output() onCloseEvent: EventEmitter<boolean> = new EventEmitter<boolean>(false)
  @Output() onFieldBlurEvent: EventEmitter<{ fieldName: string, value: any }> = new EventEmitter<{ fieldName: string, value: any }>()
  // @Output() onSelectAddEvent: EventEmitter<{ UicType: UICMasterType, data: any }> = new EventEmitter<{ UicType: UICMasterType, data: any }>();
  @Input() isFormModal: boolean = true;
  @Input() openForm: boolean = false
  @Input() customContentTemplate?: TemplateRef<any>; // Custom template for additional content

  // Multiple Forms Feature
  @Input() enableMultipleForms: boolean = false;
  @Input() addFormButtonLabel: string = 'Add Form';
  @Input() showAddMultiFormButton: boolean = true;
  @Input() showRemoveMultiFormButton: boolean = true;
  @Output() onSubmitMultipleEvent: EventEmitter<any[]> = new EventEmitter<any[]>();

  // Array to hold multiple form instances
  formInstances: Array<{
    formGroup: FormGroup;
    formUIC: GenericFormUICDTO[];
    index: number;
  }> = [];

  // ✅ NEW: Track patched values during edit mode to prevent overwriting
  private patchedValues: Map<string, any> = new Map();
  private isPatchingInProgress: boolean = false;


  http = inject(HttpClient)
  User = inject(UserLoginService).User()
  isFormReady: boolean = false;
  isSubmitting: boolean = false;
  showDiscount: boolean = false;
  showTax: boolean = false;
  UicDiscountComponent = UicDiscountComponent;
  documentMasterService = inject(DocumentMasterService)
  companyRelationService = inject(CompanyRelationService);
  indvidualMasterService = inject(IndividualService);
  individualRelationService = inject(IndividualRelationService);
  contactDetailService = inject(ContactDetailService);
  addressDetailService = inject(AddressDetailService);
  destinationMasterService = inject(DestinationMasterService);
  docMasterService = inject(DocMasterService);
  imageCompressionService = inject(ImageCompressionService);
  router = inject(Router)
  route = inject(ActivatedRoute)

  // ✅ NEW: Refactored UIC Form Services
  private formBuilderService = inject(FormBuilderService);
  private dependentFieldService = inject(DependentFieldService);
  private dataFetchService = inject(DataFetchService);
  private uicValidationService = inject(UICValidationService);

  MasterId: number = 0
  localPrice!: number;
  isModalOpen: boolean = false;

  disabledSelectNAddField: boolean = false;
  isAddressInEditMode: boolean = false;

  @ViewChild('addFormBtnContainer') addFormBtnContainer?: ElementRef;

  private ngZone = inject(NgZone);

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['gFormUIC'] && this.gFormUIC.length > 0)) {
      this.ReadyUICForm();
    }
  }


  ReadyUICForm() {
    // ✅ Use refactored implementation if flag is enabled
    if (this.useRefactoredImplementation) {
      console.log('🎯 Using REFACTORED implementation');
      this.ReadyUICFormRefactored();
      return;
    }

    // 🔒 Deep-clone the incoming @Input() config into our OWN working copy before
    // touching anything. The parent component (e.g. indent-master-2) fetches its
    // GenericFormUIC array ONCE in ngOnInit and reuses that SAME array/object graph
    // for every open of an *ngIf-toggled offcanvas/modal - only the uic-form CHILD
    // gets destroyed and recreated on close, not the parent's array. Every field
    // object here is a live, mutable runtime record (isLoading/dataLoaded/data
    // caches, and jsexecutor scripts like itemIdPatch that rewrite dataFetchFrom in
    // place) - without cloning, all of that runtime state is written straight onto
    // the parent's long-lived config and survives close/reopen, so the SECOND time
    // a record is edited, fields can look "already loaded" from the FIRST edit and
    // skip the fetch/patch logic that actually resolves the saved value. Cloning
    // once here, up front, means every fresh open starts from a truly clean slate.
    this.gFormUIC = JSON.parse(JSON.stringify(this.gFormUIC)) as GenericFormUICDTO[];

    this.formInstances = []; // Clear existing
    this.jsExecutorWired.clear();
    // Start from the parent's up-front signal (see isEditForm) rather than always false -
    // patchSingleFormWithDependencies still flips this true itself once patching actually
    // starts, this just closes the gap before that point.
    this.isEditMode = this.isEditForm;
    // Single form logic (existing)
    const group: { [key: string]: FormControl } = {};
    this.isSubmitting = false;

    // Collect API calls to batch them
    const apiCalls: { [key: string]: any } = {};

    this.gFormUIC.forEach(f => {

      let dependentFields = this.gFormUIC.filter(i => i.dependentOn && i.dependentOn!.trim().length > 0 && i.dependentOn.toLowerCase() == f.dbFeildName.toLowerCase())
      f.dependentFields = dependentFields

      const validators: any[] = [];

      if (f.validationReq === 1 && f.isUIVisible == 1) {
        validators.push(Validators.required);
      }

      if (f.validationPattern && f.validationPattern.startsWith('^') && f.isUIVisible == 1) {

        validators.push(Validators.pattern(new RegExp(f.validationPattern)));

      }

      if (f.validationPattern === 'noFutureDate' && f.uiInputType?.toLowerCase() === 'date' && f.isUIVisible == 1) {
        validators.push((control: FormControl) => {
          if (!control.value) return null;
          return control.value > new Date().toISOString().split('T')[0] ? { noFutureDate: true } : null;
        });
      }

      if (f.validationPattern === 'noPastDate' && f.uiInputType?.toLowerCase() === 'date' && f.isUIVisible == 1) {
        validators.push((control: FormControl) => {
          if (!control.value) return null;
          return control.value < new Date().toISOString().split('T')[0] ? { noPastDate: true } : null;
        });
      }

      let dVal: any = f.defaultValue;
      if (f.uiInputType?.toLowerCase() !== "select" && f.uiInputType?.toLowerCase() !== "selectnadd") {

        if (f.formDataType?.toLowerCase() === 'number') {
          dVal = dVal != null && dVal != undefined && dVal.trim() != '' ? Number(dVal) : 0;
        }
        else if (f.formDataType?.toLowerCase() === 'string') {
          dVal = dVal ?? '';
          console.log(f.defaultValue);

        }
        else if (f.formDataType?.toLowerCase() === 'date') {
          dVal = dVal != null && dVal != undefined && dVal.trim() != '' ? dVal : f.otherDetails?.toLowerCase() === 'patchdate' ? formatDateForInput(new Date()) : null;
        }

        group[f.dbFeildName!] = new FormControl(dVal, validators);


      } else {
        group[f.dbFeildName!] = new FormControl(dVal?.trim() == '' ? null : dVal, validators);
      }

      // Skip dependent api fields here too (see dbquery skip above): eagerly loading
      // them now runs formatAPIURL() before the parent control has a real value, so
      // {{DependentOnId}} resolves to 0. That response can arrive after the correctly
      // parented fetch triggered later during patch (patchSingleFormWithDependencies /
      // loadApiForField) and overwrite it with stale/empty data - e.g. vendorId's
      // ReadByItemId?ItemId=0 call clobbering the ReadByItemId?ItemId=<realItemId> data,
      // leaving the saved supplier unselectable in the UI even though it patched correctly.
      // Non-select "api" fields (text/number) exist solely to auto-fill a blank control
      // on create - see the "only patch if currently empty" guard below at the forkJoin
      // response handler. In edit mode the field already has a real saved value, so eagerly
      // calling this endpoint (e.g. GetMaxNumberByDocType) on every modal open is pure waste
      // and, worse, races the real patched value if the response is slow to resolve.
      const isCreateOnlyDefaultField = f.uiInputType?.toLowerCase() !== 'select' && f.uiInputType?.toLowerCase() !== 'selectnadd';
      if (f.searchable != 1 && f.dataFetchType && f.dataFetchType!.toLowerCase() == 'api' &&
          (!f.dependentOn || f.dependentOn.trim() === '') &&
          !(this.isEditMode && isCreateOnlyDefaultField)) {
        const apiUrl = this.formatAPIURL(f);
        if (!apiUrl) {
          console.warn(`[UIC-FORM] Skipping API fetch for field "${f.dbFeildName}" - DataFetchFrom is not configured`);
        } else {
          f.isLoading = true;
          apiCalls[f.dbFeildName] = this.http.get(apiUrl).pipe(
            catchError(err => {
              console.error(`[UIC-FORM] API failed for field "${f.dbFeildName}":`, err);
              f.isLoading = false;
              return of(null);
            })
          );
        }
      }

      if (f.uiInputType?.toLowerCase() == 'searchnadd') {

      }

    });



    this.gFormGroup = new FormGroup(group);

    // Auto-open tax/discount toggles when gstrate/discountRate are patched with value > 0
    this.gFormGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(values => {
      const gstRateKey = Object.keys(values).find(k => k.trim().toLowerCase() === 'gstrate');
      if (gstRateKey && Number(values[gstRateKey]) > 0) {
        this.showTax = true;
      }

      const discountRateKey = Object.keys(values).find(k => k.trim().toLowerCase() === 'discountrate');
      if (discountRateKey && Number(values[discountRateKey]) > 0) {
        this.showDiscount = true;
      }
    });

    if (this.enableMultipleForms) {
      this.formInstances.push({
        formGroup: this.gFormGroup,
        formUIC: this.gFormUIC, // Note: For cloning later, use JSON.parse(JSON.stringify(this.gFormUIC)) in the addForm() button
        index: 0
      });
    }

    // Batch all API calls with forkJoin
    if (Object.keys(apiCalls).length > 0) {
      forkJoin(apiCalls).pipe(takeUntil(this.destroy$)).subscribe({
        next: (results) => {
          Object.keys(results).forEach(dbFieldName => {
            const f = this.gFormUIC.find(field => field.dbFeildName === dbFieldName);
            if (f) {
              const res: any = results[dbFieldName];
              if (res === null) return; // API failed for this field, skip it

              // Preserve the option object for the value already sitting in the control (e.g.
              // seeded by the parent from row data — see purchase-order.ts's openModal() —
              // synchronously right after this eager forkJoin fires, before its response
              // arrives) so an "available/pending only" endpoint - e.g. GetAvailableIndent,
              // which excludes the indent already linked to the PO being edited - doesn't wipe
              // out the one entry ng-select needs to render that existing selection's label.
              // This mirrors loadApiForField()'s patch-time preservedItem handling, needed here
              // too because THIS eager fetch (fired unconditionally for non-dependent select
              // fields, even in edit mode - see the isCreateOnlyDefaultField check above, which
              // only skips plain text/number fields) can resolve before, or instead of, that
              // patch-time call ever running (loadApiForField() skips re-fetching once a field
              // is already isLoading/dataLoaded from this eager call).
              const bindKey = (f.patchPropertyName || 'key').trim();
              const selectedValueForPreserve = this.gFormGroup.get(f.dbFeildName!)?.value;
              const hasSelectedValueForPreserve = selectedValueForPreserve !== null && selectedValueForPreserve !== undefined && `${selectedValueForPreserve}`.trim() !== '';
              const preservedItem = hasSelectedValueForPreserve
                ? (Array.isArray(f.data) ? f.data : []).find((item: any) => {
                    const v = this.getObjectValueCaseInsensitive(item, bindKey);
                    return v !== undefined && v !== null && `${v}` === `${selectedValueForPreserve}`;
                  })
                : null;

              if (Array.isArray(res?.items) && res['items'].length > 0) {
                f.data = res.items.map((item: any) => {
                  const patchedKey = f.patchPropertyName
                    ? Object.keys(item).find(key =>
                      key.trim().toLowerCase() === f.patchPropertyName!.trim().toLowerCase()
                    )
                    : undefined;

                  if (patchedKey && f.formDataType?.trim().toLowerCase() === 'string') {
                    return {
                      ...item,
                      [patchedKey]: String(item[patchedKey])
                    };
                  }
                  return item;
                });

                if (preservedItem && Array.isArray(f.data)) {
                  const stillPresent = f.data.some((item: any) => {
                    const v = this.getObjectValueCaseInsensitive(item, bindKey);
                    return v !== undefined && v !== null && `${v}` === `${selectedValueForPreserve}`;
                  });
                  if (!stillPresent) {
                    f.data = [preservedItem, ...f.data];
                  }
                }

                // if (f.defaultValue && f.defaultValue?.trim() != '' && this.gFormGroup.get(f.dbFeildName)?.value && this.gFormGroup.get(f.dbFeildName)?.value.trim() == '') {
                //   this.gFormGroup.get(f.dbFeildName)?.patchValue(f.defaultValue);
                // }
                const control = this.gFormGroup.get(f.dbFeildName);
                const value = control?.value;

                if (
                  f.defaultValue &&
                  typeof f.defaultValue === 'string' &&
                  f.defaultValue.trim() !== '' &&
                  typeof value === 'string' &&
                  value.trim() === ''
                ) {
                  control?.patchValue(f.defaultValue);
                }
              }
              else {
                f.data = [res];
                if (
                  f.uiInputType?.toLowerCase() == 'text' &&
                  this.gFormGroup.get(f.dbFeildName) &&
                  (typeof this.gFormGroup.get(f.dbFeildName)?.value === 'string' ? this.gFormGroup.get(f.dbFeildName)?.value.trim() === '' : !this.gFormGroup.get(f.dbFeildName)?.value)
                ) {
                  this.gFormGroup.get(f.dbFeildName)?.patchValue(res[f.patchPropertyName.trim()]);
                }
              }
              f.isLoading = false;
            }
          });
        },
        error: (err) => {
          Object.keys(apiCalls).forEach(dbFieldName => {
            const f = this.gFormUIC.find(field => field.dbFeildName === dbFieldName);
            if (f) f.isLoading = false;
          });
        }
      });
    }
    this.gFormUIC.forEach(f => {
      // Initialize dataLoaded flag for all fields
      if (!f.hasOwnProperty('dataLoaded')) {
        f.dataLoaded = false;
      }

      // Load data for dbquery fields at initialization — skip dependent fields
      // (they have no meaningful data until the parent value is known; loading them
      // with DependentOnId=0 creates a race condition that overwrites the correctly
      // patched data during edit mode)
      if (f.dataFetchType && f.dataFetchType.toLowerCase() == 'dbquery' &&
          (!f.dependentOn || f.dependentOn.trim() === '')) {
        // dynamicQuery() no-ops when enableMultipleForms is true (it only targets
        // this.gFormGroup/this.gFormUIC), but this initial row IS row 0 of
        // formInstances in that mode — use dynamicQueryForInstance so it actually loads.
        if (this.enableMultipleForms) {
          this.dynamicQueryForInstance(null, f, this.formInstances[0], false);
        } else {
          this.dynamicQuery(null, f, false);
        }
      }

      if (f.searchable && f.searchable == 1 && f.dataFetchType && f.dataFetchType.toLowerCase() == 'api') {

        if (f.dataFetchType!.toLowerCase() == 'api') {

          f.isLoading = true;
          const apiUrl = this.formatAPIURL(f, '');
          console.log(`📡 [UIC-FORM] Making initial API call for searchable field ${f.dbFeildName} with empty search term. URL:`, apiUrl);

          this.http.get(this.formatAPIURL(f, '')).pipe(takeUntil(this.destroy$)).subscribe({
            next: (res: any) => {
              console.log(`✅ [UIC-FORM] API response for ${f.dbFeildName}:`, res);
              this.gFormUIC = this.gFormUIC.map(i => {
                if (i.dbFeildName == f.dbFeildName) {
                  i.data = [];
                  i.data = res.items
                  i.isLoading = false;
                  // .map((item: any) => {
                  //   let value = item[f.patchPropertyName];
                  //   if (f.formDataType?.trim().toLowerCase() === 'string' && value != null) {
                  //     value = value.toString();
                  //   }
                  //   item[f.patchPropertyName] = value
                  //   return item;
                  // });
                }
                return i
              })
              f.isLoading = false;
            },
            error: (err) => {
              console.error(`❌ [UIC-FORM] API error for ${f.dbFeildName}:`, err);
              f.isLoading = false;
            }
          })
        }
        this.gFormGroup.get(f.dbFeildName)?.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((searchTerm) => {
          console.log('searchTerm : ', searchTerm);

          if (f.dependentFields.length > 0) {

            if (!f.uiInputType?.trim().includes('select')) {
              f.dependentFields.forEach(uicFields => {
                if (uicFields.dataFetchType!.toLowerCase() == 'api') {
                  this.http.get(this.formatAPIURL(uicFields, searchTerm)).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
                    this.gFormUIC = this.gFormUIC.map(i => {
                      if (i.dbFeildName == uicFields.dbFeildName) {
                        i.data = [];
                        i.data = res.items
                      }
                      return i
                    })
                  })
                }
                else if (uicFields.dataFetchType!.toLowerCase() == 'patch') {
                  let data = f.data;

                  if (uicFields.patchPropertyName.trim() != '') {



                    const formValue = this.gFormGroup.get(f.dbFeildName)!.value?.toString().toLowerCase();

                    const matchedObject = data.find(i => {
                      const matchingKey = Object.keys(i).find(
                        key => key.toLowerCase() === f.patchPropertyName.toLowerCase()
                      );
                      return matchingKey && i[matchingKey]?.toString().toLowerCase() === formValue;
                    });

                    if (matchedObject) {
                      let value;
                      const matchedKey = Object.keys(matchedObject).find(key =>
                        key.toLowerCase() === uicFields.patchPropertyName.toLowerCase()
                      );

                      if (matchedKey) {
                        value = matchedObject[matchedKey];
                      }
                      this.gFormGroup.get(uicFields.dbFeildName)?.patchValue(value);
                    } else {
                      this.gFormGroup.get(uicFields.dbFeildName)?.patchValue(null);
                    }

                  } else {
                    let value = data.find(i => i.key == this.gFormGroup.get(f.dbFeildName)!.value).value
                    this.gFormGroup.get(uicFields.dbFeildName)?.patchValue(value)
                  }
                } else if (uicFields.dataFetchType!.toLowerCase() == 'apinpatch' && this.formatAPIURL(uicFields, searchTerm)) {
                  uicFields.isLoading = true;
                  this.http.get(this.formatAPIURL(uicFields, searchTerm))
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                      next: (res: any) => {
                        const first = Array.isArray(res?.items)
                          ? res.items[0]
                          : Array.isArray(res)
                            ? res[0]
                            : res;

                        let value: any = null;
                        const patchKey = (uicFields.patchPropertyName ?? '').trim().toLowerCase();

                        if (first) {
                          if (patchKey) {
                            const matchedKey = Object.keys(first).find(
                              k => k.toLowerCase() === patchKey
                            );
                            value = matchedKey ? first[matchedKey] : (first.value ?? first.key ?? null);
                          } else {
                            value = first.value ?? first.key ?? first;
                          }
                        }

                        this.gFormGroup.get(uicFields.dbFeildName)?.patchValue(value);
                        uicFields.isLoading = false;
                      },
                      error: () => {
                        uicFields.isLoading = false;
                      }
                    });
                } else if (uicFields.dataFetchType!.toLowerCase() == 'dbquery') {
                  this.dynamicQuery(this.gFormGroup.get(f.dbFeildName)?.value, uicFields, false);
                }

              })
            } else {

            }
          }
        })
      }
      else if (f.uiInputType?.toLowerCase() === 'toggle' && f.dependentOn && f.dependentOn != '') {
        console.log(this.gFormGroup.get('Price'));
        console.log(this.gFormGroup.get('price'));

        this.gFormGroup.get(f.dependentOn.toLowerCase())?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {

          this.localPrice = this.gFormGroup.get(f.dependentOn?.toLocaleLowerCase()!)?.value;
          console.log(this.localPrice);

        })
      } else if (f.uiInputType?.toLowerCase() === 'select' && f.searchable !== 1) {



        this.gFormGroup.get(f.dbFeildName)?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
          console.log('Feild : ', f.dbFeildName);
          console.log('value : ', value);
          console.log('deffvalue : ', f.defaultValue);


        })
      }

    })

    // Setup JS Executor listeners for all jsexecutor fields
    const primaryFormInstance = this.enableMultipleForms ? this.formInstances[0] : null;
    this.gFormUIC.forEach(field => {
      if (field.dataFetchType?.toLowerCase() === 'jsexecutor' && field.dataFetchFrom) {
        if (primaryFormInstance) {
          this.setupJSExecutorListener(field, primaryFormInstance);
        } else {
          this.setupJSExecutorListener(field);
        }
      }
    });

    this.isFormReady = true;
    this.formReady.emit(this.isFormReady)
  }

  /**
   * ✨ NEW REFACTORED METHOD - Uses new services (Strategy Pattern)
   * This method will gradually replace ReadyUICForm()
   * Can be enabled via a flag for testing
   */
  ReadyUICFormRefactored() {
    console.log('🎯 [Refactored] ReadyUICForm called with', this.gFormUIC.length, 'fields');

    this.formInstances = []; // Clear existing
    this.isSubmitting = false;

    // ✅ Step 1: Use FormBuilderService to create form
    const { formGroup, formFields } = this.formBuilderService.buildFormGroup(this.gFormUIC);
    this.gFormGroup = formGroup;

    // Auto-open tax/discount toggles when gstrate/discountRate are patched with value > 0
    this.gFormGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(values => {
      const gstRateKey = Object.keys(values).find(k => k.trim().toLowerCase() === 'gstrate');
      if (gstRateKey && Number(values[gstRateKey]) > 0) {
        this.showTax = true;
      }

      const discountRateKey = Object.keys(values).find(k => k.trim().toLowerCase() === 'discountrate');
      if (discountRateKey && Number(values[discountRateKey]) > 0) {
        this.showDiscount = true;
      }
    });

    console.log('✅ [Refactored] FormGroup created with', formFields.length, 'fields');

    // ✅ Step 2: Setup dependencies using DependentFieldService
    this.dependentFieldService.setupDependencies(
      formFields,
      this.gFormGroup,
      {
        companyId: this.User.companyId,
        branchId: this.User.loggedInBranchId || 0,
        userId: this.User.userId,
        masterId: this.MasterId,
        masterType: this.gFormUIC[0]?.masterType
      },
      this.destroy$
    );

    console.log('✅ [Refactored] Dependencies setup complete');

    // ✅ Step 3: Load initial data for non-searchable API fields
    formFields.forEach(field => {
      if (field.searchable !== 1 &&
        field.dataFetchType?.toLowerCase() === 'api' &&
        field.isVisible) {

        field.loading = true;

        const context = DataFetchContext.create(
          field,
          this.gFormGroup,
          {
            companyId: this.User.companyId,
            branchId: this.User.loggedInBranchId || 0,
            userId: this.User.userId,
            masterId: this.MasterId
          },
          formFields
        );

        this.dataFetchService.fetchData(context).subscribe({
          next: (data) => {
            field.options = Array.isArray(data) ? data : [data];
            field.loading = false;
            console.log(`✅ [Refactored] Loaded data for ${field.dbFeildName}:`, field.options?.length, 'items');
          },
          error: (err) => {
            console.error(`❌ [Refactored] Error loading data for ${field.dbFeildName}:`, err);
            field.loading = false;
          }
        });
      }
    });

    // ✅ Step 4: Setup for multiple forms if enabled
    if (this.enableMultipleForms) {
      this.formInstances.push({
        formGroup: this.gFormGroup,
        formUIC: this.gFormUIC,
        index: 0
      });
    }

    this.isFormReady = true;
    this.formReady.emit(this.isFormReady);

    console.log('🎉 [Refactored] Form is ready!');
  }

  getFormControl(controlName: string) {
    return this.gFormGroup.get(controlName) as FormControl
  }

  getTodayDate(): string {
    const d = new Date();
    return d.toISOString().split('T')[0];
  }

  // ============== MULTIPLE FORMS METHODS ==============

  addFormAndScroll() {
    this.addForm();
    setTimeout(() => {
      this.addFormBtnContainer?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 50);
  }

  removeForm(index: number) {
    if (this.formInstances.length > 1) {
      this.formInstances.splice(index, 1);
      // Re-index remaining forms
      this.formInstances.forEach((instance, idx) => {
        instance.index = idx;
      });
    }
  }

  setupFormInstanceSubscriptions(formGroup: FormGroup, formUIC: GenericFormUICDTO[], formIndex: number) {
    formUIC.forEach(f => {
      // Handle toggle dependencies
      if (f.uiInputType?.toLowerCase() === 'toggle') {
        if (f.dependentOn) {
          formGroup.get(f.dependentOn)?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            formGroup.get(f.dbFeildName)?.patchValue(0);
          });
        }
      }

      // Handle select value changes for dependent fields
      if (f.dependentFields && f.dependentFields.length > 0) {
        formGroup.get(f.dbFeildName)?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
          this.handleDependentFieldsForInstance(f, value, formGroup, formUIC, formIndex);
        });
      }
    });
  }

  handleDependentFieldsForInstance(parentField: GenericFormUICDTO, value: any, formGroup: FormGroup, formUIC: GenericFormUICDTO[], formIndex: number) {
    if (!parentField.dependentFields || parentField.dependentFields.length === 0) return;

    this.resetDependentValues(parentField.dbFeildName!, formGroup, formUIC);

    parentField.dependentFields.forEach(dependent => {
      const dependentField = formUIC.find(f => f.dbFeildName === dependent.dbFeildName);
      if (!dependentField) return;

      if (value && typeof value === 'object') {
        // Selected item from dropdown
        if (dependent.dataFetchType?.toLowerCase() === 'patch' && dependent.patchPropertyName) {
          const patchValue = value[dependent.patchPropertyName];
          formGroup.get(dependent.dbFeildName)?.patchValue(patchValue ?? null);
        } else if (dependent.dataFetchType?.toLowerCase() === 'api') {
          // Fetch dependent data via API
          dependentField.isLoading = true;
          const apiUrl = this.formatAPIURL(dependentField);
          this.http.get(apiUrl).pipe(takeUntil(this.destroy$)).subscribe({
            next: (res: any) => {
              dependentField.data = res;
              dependentField.isLoading = false;
            },
            error: (err) => {
              console.error('Error fetching dependent data for form', formIndex, err);
              dependentField.isLoading = false;
            }
          });
        }
      } else {
        // Value cleared or typed text
        if (dependent.dataFetchType?.toLowerCase() === 'patch') {
          const defaultValue = dependent.formDataType?.toLowerCase() === 'number' ? 0 : null;
          formGroup.get(dependent.dbFeildName)?.patchValue(defaultValue);
        }
      }
    });
  }

  getFormControlForInstance(instance: any, controlName: string) {
    return instance.formGroup.get(controlName) as FormControl;
  }

  onSubmitMultipleForms() {
    // Validate all forms
    let allValid = true;
    this.formInstances.forEach(instance => {
      instance.formGroup.markAllAsTouched();
      if (!instance.formGroup.valid) {
        allValid = false;
      }
    });

    if (!allValid) {
      return;
    }

    // Collect data from all forms
    const allFormsData = this.formInstances.map(instance => instance.formGroup.getRawValue());

    // Emit to parent component
    this.onSubmitMultipleEvent.emit(allFormsData);
  }

  // ============== END MULTIPLE FORMS METHODS ==============

  onCheckBoxChange(event: any, formControl: string, targetFormGroup?: FormGroup) {
    const value = event.target.checked ? 1 : 0;
    // In enableMultipleForms mode each row has its own FormGroup (formInstance.formGroup).
    // Callers inside the multi-form template pass that instance explicitly so the change
    // patches the correct row instead of falling through to the single shared gFormGroup.
    (targetFormGroup ?? this.gFormGroup).get(formControl)?.patchValue(value);
  }

  handleToggleDependOn(f: GenericFormUICDTO) {
    this.gFormGroup.get(f.dependentOn!)?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.localPrice = this.gFormGroup.get(f.dependentOn!)?.value;
      console.log(this.localPrice);

    })
  }

  private searchTimeout: any;
  private dynamicQueryTimeout: any;

  /**
   * ✨ REFACTORED: Search handler using DataFetchService
   * Handles searchable field input with debouncing
   */
  onSearch(event: { term: string; items: any[]; }, uicForm: GenericFormUICDTO) {
    // ✅ Use refactored version if flag is enabled
    if (this.useRefactoredImplementation) {
      this.onSearchRefactored(event, uicForm);
      return;
    }

    // 📝 LEGACY implementation
    uicForm.searchTerm = event.term
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.gFormGroup.get(uicForm.dbFeildName)?.patchValue(event.term);
      uicForm.isLoading = true;
      this.http.get(this.formatAPIURL(uicForm, event.term)).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res: any) => {
          console.log('[Legacy] API response for', uicForm.dbFeildName, ':', res);


          // ✅ Update the entire array reference for change detection
          let dropdownData: any[] = [];
          if (Array.isArray(res)) {
            dropdownData = res;
          } else if (res && res.items && Array.isArray(res.items)) {
            dropdownData = res.items;
          }



          const normalizedData = (dropdownData).map((item: any) => {
            const patchedKey = Object.keys(item).find(key =>
              key.trim().toLowerCase() === uicForm.patchPropertyName.trim().toLowerCase()
            );

            if (patchedKey && uicForm.formDataType?.trim().toLowerCase() === 'string') {
              return {
                ...item,
                [patchedKey]: String(item[patchedKey])
              };
            }

            return item;
          });



          this.gFormUIC = this.gFormUIC.map(i => {
            if (i.dbFeildName.trim().toLowerCase() === uicForm.dbFeildName.toLowerCase()) {
              i.data = normalizedData;
              i.isLoading = false;
            }
            return i;
          });

          console.log('[Legacy] Updated dropdown with', normalizedData.length, 'items');

          if (normalizedData.length === 0 && uicForm.dependentFields && uicForm.dependentFields.length > 0) {
            //this.handleTypedTextForDependents(uicForm, event.term);
          }

        },
        error: (err) => {
          console.error('[Legacy] API error for', uicForm.dbFeildName, ':', err);
          this.gFormUIC = this.gFormUIC.map(i => {
            if (i.dbFeildName === uicForm.dbFeildName) {
              i.isLoading = false;
            }
            return i;
          });
        }
      })

    }, 300);
  }

  /**
   * ✅ NEW: Refactored search using DataFetchService
   */
  onSearchRefactored(event: { term: string; items: any[]; }, uicForm: GenericFormUICDTO) {
    uicForm.searchTerm = event.term;
    clearTimeout(this.searchTimeout);

    this.searchTimeout = setTimeout(() => {
      this.gFormGroup.get(uicForm.dbFeildName)?.patchValue(event.term);
      uicForm.isLoading = true;

      // ✅ Create context and use DataFetchService
      const field = FormField.fromDTO(uicForm);
      const context = DataFetchContext.create(
        field,
        this.gFormGroup,
        {
          companyId: this.User.companyId,
          branchId: this.User.loggedInBranchId || 0,
          userId: this.User.userId,
          masterId: this.MasterId,
          masterType: this.gFormUIC[0]?.masterType
        }
      ).withSearchTerm(event.term);

      this.dataFetchService.fetchData(context).subscribe({
        next: (data: any) => {
          console.log('[Refactored] Raw API response for', uicForm.dbFeildName, ':', data);

          // ✅ Handle both formats: wrapped object or direct array - SAME AS LEGACY
          let rawData: any[] = [];

          if (data && data.items && Array.isArray(data.items)) {
            // Format 1: { items: [...] }
            rawData = data.items;
            console.log('[Refactored] Extracted from data.items:', rawData.length, 'items');
          } else if (data && Array.isArray(data)) {
            // Format 2: Direct array [...]
            rawData = data;
            console.log('[Refactored] Direct array:', rawData.length, 'items');
          } else if (data && data.data && Array.isArray(data.data)) {
            // Format 3: { data: [...] }
            rawData = data.data;
            console.log('[Refactored] Extracted from data.data:', rawData.length, 'items');
          } else {
            // No data or invalid format
            rawData = [];
            console.warn('[Refactored] No valid data format found, using empty array');
          }

          // Normalize data (ensure string types if needed) - SAME AS LEGACY
          const normalizedData = rawData.map((item: any) => {
            const patchedKey = Object.keys(item).find(key =>
              key.trim().toLowerCase() === uicForm.patchPropertyName.trim().toLowerCase()
            );

            if (patchedKey && uicForm.formDataType?.trim().toLowerCase() === 'string') {
              return {
                ...item,
                [patchedKey]: String(item[patchedKey])
              };
            }

            return item;
          });

          // ✅ Update the data in the gFormUIC array to ensure change detection
          this.gFormUIC = this.gFormUIC.map(f => {
            if (f.dbFeildName === uicForm.dbFeildName) {
              f.data = normalizedData;
              f.isLoading = false;
            }
            return f;
          });

          console.log(`✅ [Refactored] Search completed for ${uicForm.dbFeildName}:`, normalizedData.length, 'items');
        },
        error: (err) => {
          console.error(`❌ [Refactored] Search failed for ${uicForm.dbFeildName}:`, err);
          // Update loading state in array
          this.gFormUIC = this.gFormUIC.map(f => {
            if (f.dbFeildName === uicForm.dbFeildName) {
              f.isLoading = false;
            }
            return f;
          });
        }
      });
    }, FORM_CONFIG.SEARCH_DEBOUNCE_TIME);
  }

  onClose() {
    this.onCloseEvent.emit()
  }

  onSubmit(proceedToSecondaryAction: boolean = false) {
    // If multiple forms mode is enabled, use different submit logic
    if (this.enableMultipleForms) {
      this.onSubmitMultipleForms();
      return;
    }

    // Check if address is in edit mode
    if (this.isAddressInEditMode) {
      alert('Please save or cancel the address changes before submitting the form.');
      return;
    }

    if (this.gFormGroup.invalid) {

      this.gFormGroup.markAllAsTouched();   // ✅ force validation UI
      this.gFormGroup.updateValueAndValidity();

      Object.keys(this.gFormGroup.controls).forEach(key => {
        const control = this.gFormGroup.get(key);
        if (control?.invalid) {
          console.log(`Field "${key}" errors:`, control.errors);
        }
      });

      return;
    }
    this.onSubmitEvent.emit(proceedToSecondaryAction)
  }

  private escapeFilterValue(value: any, f: GenericFormUICDTO): string {
    const str = value?.toString() ?? '';
    return (f?.dataFetchType || '').trim().toLowerCase() === 'dbquery'
      ? str.replace(/'/g, "''")
      : encodeURIComponent(str);
  }

  // ✅ NEW METHOD: For multiple forms - accepts FormGroup parameter
  formatAPIURLForInstance(
    f: GenericFormUICDTO,
    filterValue: string = '',
    formGroup: FormGroup
  ): string {

    let url = (f.dataFetchFrom || '').toString();
    const urlId = this.route.snapshot.paramMap.get('id') ?? '0';

    // ✅ FIXED: Properly handle dependentOn and dependentOnKey for form instances
    let dependentValue: any = null;

    if (f.dependentOn) {
      if (f.dependentOnKey) {
        // Get the selected value from dependentOn field
        const selectedValue = formGroup.get(f.dependentOn)?.value;

        // Find the parent field config to get its data
        const parentField = this.gFormUIC.find(field => field.dbFeildName === f.dependentOn);

        if (parentField && parentField.data && selectedValue) {
          // Find the selected item in parent field's data
          const selectedItem = parentField.data.find((item: any) => {
            // Match by patchPropertyName (the value field)
            const patchKey = Object.keys(item).find(key =>
              key.toLowerCase() === parentField.patchPropertyName?.toLowerCase()
            );
            return patchKey && item[patchKey] === selectedValue;
          });

          if (selectedItem) {
            // Extract the dependentOnKey value from the selected item
            const dependentKey = Object.keys(selectedItem).find(key =>
              key.toLowerCase() === f.dependentOnKey?.toLowerCase()
            );
            dependentValue = dependentKey ? selectedItem[dependentKey] : selectedValue;
          } else {
            dependentValue = selectedValue;
          }
        } else {
          // Fallback to getting value directly from dependentOnKey field
          dependentValue = formGroup.get(f.dependentOnKey)?.value;
        }
      } else {
        // No dependentOnKey specified, use dependentOn field value directly
        dependentValue = formGroup.get(f.dependentOn)?.value;
      }
    }

    // ✅ System placeholders
    const dynamicValues: Record<string, any> = {
      '{{CompanyId}}': this.User.companyId,
      '{{urlId}}': urlId,
      '{{LoggedInCompanyId}}': this.User.companyId,
      '{{UserId}}': this.User.userId.toString(),
      '{{BranchId}}': this.User.loggedInBranchId,
      '{{SoftwareId}}': environment.SoftwareId,
      '{{FilterValue}}': filterValue
        ? this.escapeFilterValue(filterValue, f)
        : '{{FilterValue}}',
      '{{hLevel}}': formGroup.get('hLevel')?.value || 0,
      '{{itemId}}': formGroup.get('itemId')?.value || 0,
      '{{SelectedBranchId}}':
        formGroup.get('branchId')?.value ?? this.User.loggedInBranchId,
      '{{DependentOnId}}':
        dependentValue !== undefined &&
          dependentValue !== null &&
          dependentValue !== ''
          ? dependentValue
          : 0
    };

    // ✅ Replace system placeholders first
    for (const key in dynamicValues) {
      url = url.replaceAll(key, dynamicValues[key]?.toString() ?? '');
    }

    // ✅ Replace remaining form-field placeholders dynamically
    const placeholderRegex = /\{\{(\w+)\}\}/g;
    let match;

    while ((match = placeholderRegex.exec(f.dataFetchFrom || '')) !== null) {
      const fieldName = match[1];
      const camelCase =
        fieldName.charAt(0).toLowerCase() + fieldName.slice(1);

      // Skip system placeholders already handled
      if (dynamicValues[`{{${fieldName}}}`] !== undefined) {
        continue;
      }

      const value = formGroup.get(camelCase)?.value;

      if (value !== null && value !== undefined) {
        url = url.replaceAll(`{{${fieldName}}}`, value.toString());
        console.log(`✅ Replaced {{${fieldName}}} with ${value}`);
      } else {
        console.warn(`⚠️ No value found for {{${fieldName}}}`);
      }
    }

    return url;
  }


  formatAPIURL(f: GenericFormUICDTO, filterValue?: string): string {
    let url = (f.dataFetchFrom || '').toString();

    const urlId = this.route.snapshot.paramMap.get('id') ?? '0';
    console.log('Page ID:', urlId);

    // ✅ FIXED: Properly handle dependentOn and dependentOnKey
    let dependentValue: any = null;

    if (f.dependentOn) {
      if (f.dependentOnKey) {
        // Get the selected value from dependentOn field
        const selectedValue = this.gFormGroup.get(f.dependentOn)?.value;

        // Find the parent field config to get its data
        const parentField = this.gFormUIC.find(field => field.dbFeildName === f.dependentOn);

        if (parentField && parentField.data && selectedValue) {
          // Find the selected item in parent field's data
          const selectedItem = parentField.data.find((item: any) => {
            // Match by patchPropertyName (the value field)
            const patchKey = Object.keys(item).find(key =>
              key.toLowerCase() === parentField.patchPropertyName?.toLowerCase()
            );
            return patchKey && item[patchKey] === selectedValue;
          });

          if (selectedItem) {
            // Extract the dependentOnKey value from the selected item
            const dependentKey = Object.keys(selectedItem).find(key =>
              key.toLowerCase() === f.dependentOnKey?.toLowerCase()
            );
            dependentValue = dependentKey ? selectedItem[dependentKey] : selectedValue;
          } else {
            dependentValue = selectedValue;
          }
        } else {
          // Fallback to getting value directly from dependentOnKey field
          dependentValue = this.gFormGroup.get(f.dependentOnKey)?.value;
        }
      } else {
        // No dependentOnKey specified, use dependentOn field value directly
        dependentValue = this.gFormGroup.get(f.dependentOn)?.value;
      }
    }

    const dynamicValues: Record<string, any> = {
      '{{CompanyId}}': this.User.companyId,
      '{{urlId}}': urlId,
      '{{LoggedInCompanyId}}': this.User.companyId,
      '{{UserId}}': this.User.userId.toString(),
      '{{BranchId}}': this.User.loggedInBranchId,
      '{{SoftwareId}}': environment.SoftwareId,
      '{{FilterValue}}': filterValue ? this.escapeFilterValue(filterValue, f) : '{{FilterValue}}',
      '{{hLevel}}': this.gFormGroup.get("hLevel")?.value || 0,
      '{{itemId}}': this.gFormGroup.get("itemId")?.value || 0,
      '{{SelectedBranchId}}': this.gFormGroup.get("branchId")?.value ?? this.User.loggedInBranchId,
      '{{DependentOnId}}': dependentValue !== undefined && dependentValue !== null && dependentValue !== ''
        ? dependentValue
        : 0
    };

    // Replace all dynamic keys safely
    for (const key in dynamicValues) {
      const value = dynamicValues[key];
      url = url.replaceAll(key, value?.toString() ?? '');
    }

    if ((f.dataFetchType || '').trim().toLowerCase() === 'dbquery') {
      return url;
    }

    return environment.getApiUrl(url);
  }


  onNumberFocus(controlName: string) {
    const ctrl = this.gFormGroup.get(controlName);
    if (ctrl && ctrl.value === 0) {
      ctrl.patchValue('');
    }
  }

  onNumberBlur(controlName: string) {
    const ctrl = this.gFormGroup.get(controlName);
    if (ctrl && (ctrl.value === '' || ctrl.value === null || isNaN(ctrl.value))) {
      ctrl.patchValue(0);
    }
  }

  onFieldBlur(fieldName: string) {
    const value = this.gFormGroup.get(fieldName)?.value;
    this.onFieldBlurEvent.emit({ fieldName, value });
  }

  onFormKeyDown(event: any): void {
    // if (event.key !== 'Enter') return;
    const target = event.target as HTMLElement;
    const tagName = target.tagName.toLowerCase();
    const inputType = tagName === 'input' ? (target as HTMLInputElement).type : '';

    const isInsideNgSelect = target.closest('.ng-select') !== null;
    const isInsideAllowAddTag = target.closest('.allow-add-tag') !== null;
    const isSelectOrCheckbox =
      tagName === 'select' ||
      inputType === 'checkbox' ||
      (isInsideNgSelect && !isInsideAllowAddTag);

    // if (event.key === 'Enter') {
    if (isSelectOrCheckbox) {
      event.preventDefault();
      return;
    }

    // Prevent default and trigger manual submission
    event.preventDefault();
    this.onSubmit();
    // }
  }


  handleToggleSwitch(dbFeildName: string) {
    let nowVisible = false;

    if (dbFeildName.toLowerCase() == 'discount') {
      this.showDiscount = !this.showDiscount;
      nowVisible = this.showDiscount;
    } else if (dbFeildName.toLowerCase() == 'tax') {
      this.showTax = !this.showTax;
      nowVisible = this.showTax;
    }

    // Only reset the value when toggling OFF (hiding the section)
    if (!nowVisible) {
      this.gFormGroup.get(dbFeildName)?.patchValue(0);
      if (dbFeildName.toLowerCase() === 'discount') {
        this.gFormGroup.get('discountRate')?.patchValue(0);
      }
    }
  }


  onFileSelected(event: any, dbFeildName: string): void {
    const file = event.target.files[0];
    if (file) {
      this.onUpload(file, dbFeildName);
    }
  }

  // Image fields only: downscale + re-encode before the upload ever hits
  // the API. Non-image files (shouldn't happen here since the input is
  // accept="image/*", but kept defensive) pass through untouched -
  // compressImage() itself no-ops on those via the file.type check.
  async onImageFileSelected(event: any, dbFeildName: string): Promise<void> {
    const file = event.target.files[0];
    if (!file) return;
    const compressed = await this.imageCompressionService.compressImage(file);
    this.onUpload(compressed, dbFeildName);
  }

  onUpload(file: File, dbFeildName: string): void {
    const formData = new FormData();

    formData.append('File', file, file.name);
    formData.append('CompanyId', this.User.companyId.toString());
    formData.append('BranchId', this.User.loggedInBranchId.toString());
    formData.append('SoftwareId', environment.SoftwareId);

    // this.http.post<DocumentMasterDTO>(DocumentMaster_Upload, data)

    this.http.post<DocumentMasterDTO>(DocumentMaster_Upload, formData, {
      reportProgress: true,
      observe: 'events'
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.uploadProgress[dbFeildName] = Math.round((event.loaded / (event.total || 1)) * 100);
        } else if (event.type === HttpEventType.Response) {
          delete this.uploadProgress[dbFeildName];
          const imageUrl = event.body?.url;
          this.gFormGroup.get(dbFeildName)?.patchValue(imageUrl)
        }
      },
      error: () => {
        delete this.uploadProgress[dbFeildName];
        alert('Upload failed');
      }
    });



    // this.documentMasterService.DocumentMaster_Upload(formData).subscribe(
    //   (response) => {

    //     this.gFormGroup.get(dbFeildName)?.patchValue(response.url);
    //   },
    //   (error) => {
    //     console.error('Error uploading file:', error);
    //   }
    // );
  }

  previewDocument(url: string): void {
    if (url) {
      window.open(url, '_blank');
    }
  }


  // Get image src for preview
  getImageSrc(fieldName: string): string {
    const control = this.gFormGroup.get(fieldName);
    const value = control?.value?.trim();

    return value ? value : '';
  }

  // Open camera for capture
  async openCamera(fieldName: string): Promise<void> {
    try {
      this.currentCameraField = fieldName;
      this.isCameraActive = true;

      // Wait for DOM to update
      setTimeout(async () => {
        this.videoElement = document.getElementById('cameraVideo') as HTMLVideoElement;
        if (!this.videoElement) {
          console.error('Video element not found');
          this.isCameraActive = false;
          return;
        }

        // Request camera access
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false
        });

        this.videoElement.srcObject = this.mediaStream;
      }, 100);
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please check permissions.');
      this.isCameraActive = false;
    }
  }

  // Capture photo from camera
  capturePhoto(): void {
    if (!this.videoElement) {
      alert('Camera not ready. Please try again.');
      return;
    }

    // Check if video has loaded with valid dimensions
    if (this.videoElement.videoWidth === 0 || this.videoElement.videoHeight === 0) {
      alert('Camera is still loading. Please wait a moment and try again.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = this.videoElement.videoWidth;
    canvas.height = this.videoElement.videoHeight;
    const context = canvas.getContext('2d');

    if (!context) {
      alert('Unable to capture photo. Please try again.');
      return;
    }

    // Set capturing state
    this.isCapturing = true;

    // Draw the video frame to canvas
    context.drawImage(this.videoElement, 0, 0);

    // Convert canvas to blob and upload
    canvas.toBlob(async (blob) => {
      if (!blob) {
        this.ngZone.run(() => {
          alert('Unable to capture photo. Please try again.');
          this.isCapturing = false;
        });
        return;
      }
      const file = new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const compressed = await this.imageCompressionService.compressImage(file);
      // Run inside Angular zone to ensure change detection
      this.ngZone.run(() => {
        this.onUpload(compressed, this.currentCameraField);
        // Close camera after successful capture
        this.closeCamera();
      });
    }, 'image/jpeg', 0.95);
  }

  // Close camera and release stream
  closeCamera(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
    if (this.videoElement) {
      this.videoElement.srcObject = null;
    }
    this.videoElement = null;
    this.isCameraActive = false;
    this.isCapturing = false;
    this.currentCameraField = '';
  }

  uploadProgress: { [fieldName: string]: number } = {}
  AddModalRef!: NgbModalRef;
  public modalService = inject(NgbModal)
  UICFormComponent!: UicFormComponent
  uicForm: GenericFormUICDTO[] = [];
  UICFormService = inject(GenericFormUICService)
  itemMasterComponent!: ItemMasterComponent
  itemMasterService = inject(ItemMasterService)
  companyMasterService = inject(CompaniesService)

  // Camera properties
  isCameraActive: boolean = false;
  isCapturing: boolean = false;
  currentCameraField: string = '';
  videoElement: HTMLVideoElement | null = null;
  mediaStream: MediaStream | null = null;

  handleSelectNadd(inputs: GenericFormUICDTO, formInstance?: { formGroup: FormGroup; formUIC: GenericFormUICDTO[]; index: number }) {
    // Multi-form mode: the Add button belongs to a specific row (formInstance). All patching and
    // dependent-field handling must target THAT row's FormGroup. this.gFormGroup is always row 0's
    // group, so using it here made a new item from row 2+ overwrite row 1.
    // Single-form mode: no formInstance is passed, so this falls back to this.gFormGroup as before.
    const targetGroup: FormGroup = formInstance?.formGroup ?? this.gFormGroup;
    const fireSelectChange = (row: any) => formInstance
      ? this.onSelectChangeForInstance(row, inputs, formInstance)
      : this.onSelectChange(row, inputs);
    this.isModalOpen = true;
    let data: GenericFormUICReadByTenantIdRequestDTO = {
      masterType: inputs.customAttribute!,
      tenantId: this.User.companyId
    }
    this.UICFormService.GenericFormUIC_ReadByTenantId(data).pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.uicForm = res.items
      this.AddModalRef = this.modalService.open(UicFormComponent, { size: 'xl', backdrop: 'static' })
      this.UICFormComponent = this.AddModalRef.componentInstance as UicFormComponent
      this.UICFormComponent.gFormUIC = this.uicForm
      this.UICFormComponent.gFormGroup = new FormGroup({});
      this.UICFormComponent.ReadyUICForm()
      this.UICFormComponent.gFormGroup.reset()

      if (inputs.customAttribute == UICMasterType.CompanyForm) {
        this.UICFormComponent.gFormGroup.get('cName')?.patchValue(inputs.searchTerm)
      } else if (inputs.customAttribute == UICMasterType.ItemForm) {
        this.UICFormComponent.gFormGroup.get('itemDetails')?.patchValue(inputs.searchTerm)
      }

      this.UICFormComponent.onSubmitEvent.subscribe(res => {
        if (this.UICFormComponent.gFormGroup.valid) {
          Object.keys(this.UICFormComponent.gFormGroup.controls).forEach(key => {
            const control = this.UICFormComponent.gFormGroup.get(key);
            if (control && control.invalid) {
              console.log(`Field "${key}" has validation errors:`);
              console.log(control.errors);
            }
          });

          if (inputs.customAttribute == UICMasterType.CompanyForm) {
            let formData = this.UICFormComponent.gFormGroup.value as CompanyRelationCreateRequestDTO

            let companyData: CompanyRelationCreateRequestDTO = {
              cName: formData.cName ?? '',
              cCode: formData.cCode ?? '',
              cDesc: formData.cDesc ?? '',
              cAddress: formData.cAddress ?? '',
              email: formData.email ?? '',
              phone: formData.phone ?? '',
              website: formData.website ?? '',
              category: formData.category ?? '',
              subCategory: formData.subCategory ?? '',
              contactPerson: formData.contactPerson ?? '',
              cType: formData.cType ?? '',
              contactPersonDesignation: formData.contactPersonDesignation ?? '',
              gstNo: formData.gstNo ?? '',
              panNo: formData.panNo ?? '',
              msmeCategory: formData.msmeCategory ?? '',
              msmeNumber: formData.msmeNumber ?? '',
              actionUser: this.User.userId.toString(),
              otherDetail1: formData.otherDetail1 ?? "",
              otherDetail2: formData.otherDetail2 ?? "",
              otherDetail3: formData.otherDetail3 ?? "",
              otherDetail4: formData.otherDetail4 ?? "",
              otherDetail5: formData.otherDetail5 ?? "",
              otherDetail6: formData.otherDetail6 ?? "",
              otherDetail7: formData.otherDetail7 ?? '',
              otherDetail8: formData.otherDetail8 ?? '',
              otherDetail9: formData.otherDetail9 ?? '',
              otherDetail10: formData.otherDetail10 ?? '',
              otherDetail11: formData.otherDetail11 ?? '',
              otherDetail12: formData.otherDetail12 ?? '',
              otherDetail13: formData.otherDetail13 ?? '',
              otherDetail14: formData.otherDetail14 ?? '',
              otherDetail15: formData.otherDetail15 ?? '',
              transportId: ensureNumber(formData.transportId) ?? 0,
              agentId: ensureNumber(formData.agentId) ?? 0,
              salesPersonId: ensureNumber(formData.salesPersonId) ?? 0,
              tenantId: this.User.companyId,
              companyId: formData.companyId ?? 0,
              relationType: inputs.defaultValue ?? '',
              isAMCClient: formData.isAMCClient ?? 0,
              tenantBranchId: this.User.loggedInBranchId,
              currency: formData.currency ?? '',
              creditLimit: formData.creditLimit ?? 0,
              creditPeriod: formData.creditPeriod ?? '',

              stateCode: formData.stateCode ?? '',
              isDefault: formData.isDefault ?? 0
            };

            this.companyRelationService.CompanyRelation_create(companyData).pipe(takeUntil(this.destroy$)).subscribe(crres => {
              // Step 1: Create AddressDetail for CompanyRelation
              let addressData: AddressDetailCreateRequestDTO = {
                masterId: crres.relationId,
                masterType: 'CompanyRelation',
                addressType: 'Default',
                address: crres.cAddress ?? '',
                city: '',
                state: '',
                country: '',
                pincode: '',
                status: 1,
                gstNo: crres.gstNo || '',
                otherDetail1: crres.otherDetail1 || '',
                otherDetail2: crres.otherDetail2 || '',
                otherDetail3: crres.otherDetail3 || '',
                otherDetail4: crres.otherDetail4 || '',
                otherDetail5: crres.otherDetail5 || '',
                otherDetail6: crres.otherDetail6 || '',
                actionUser: this.User.userId.toString(),
                isDefault: 0,
                tenantId: this.User.companyId,
                tenantBranchId: this.User.loggedInBranchId
              };

              this.addressDetailService.AddressDetail_create(addressData).pipe(takeUntil(this.destroy$)).subscribe(addressRes => {
                console.log('Address details created successfully', addressRes);
              }, addressError => {
                console.error('Error creating address details', addressError);
              });

              // Step 2: Update form with the created CompanyRelation
              inputs.data = [];
              inputs.data.push(crres);
              targetGroup.get(inputs.dbFeildName)?.patchValue(crres.relationId);
              this.AddModalRef.close();

              // Step 3: Call onSelectChange with the response
              fireSelectChange(crres);
            });
          } else if (inputs.customAttribute == UICMasterType.ItemForm) {

            let formData = this.UICFormComponent.gFormGroup.value as ItemMasterUpdateRequestDTO
            let item: ItemMasterCreateRequestDTO = {
              ...formData,
              tenantId: this.User.companyId,
              tenantBranchId: this.User.loggedInBranchId,
              actionUser: this.User.userId.toString(),
              otherDetails1: formData.otherDetails1?.toString() ?? "",
              otherDetails2: formData.otherDetails2?.toString() ?? "",
              otherDetails3: formData.otherDetails3?.toString() ?? "",
              otherDetails4: formData.otherDetails4?.toString() ?? "",
              otherDetails5: formData.otherDetails5?.toString() ?? "",
              otherDetails6: formData.otherDetails6?.toString() ?? "",
              otherDetails7: formData.otherDetails7?.toString() ?? "",
              otherDetails8: formData.otherDetails8?.toString() ?? "",
              otherDetails9: formData.otherDetails9?.toString() ?? "",
              otherDetails10: formData.otherDetails10?.toString() ?? "",
              otherDetails11: formData.otherDetails11?.toString() ?? "",
              otherDetails12: formData.otherDetails12?.toString() ?? "",
              otherDetails13: formData.otherDetails13?.toString() ?? "",
              otherDetails14: formData.otherDetails14?.toString() ?? "",
              otherDetails15: formData.otherDetails15?.toString() ?? "",
              isDuplicate: 0
            }

            this.itemMasterService.ItemMaster_Create(item).pipe(takeUntil(this.destroy$)).subscribe(res => {
              this.AddModalRef.close()
              // Keep this row's existing options and put the new item first, so ng-select can
              // resolve its label. Deliberately NOT calling loadApiForField/loadDbQueryForField
              // here: both read this.gFormGroup (row 0 in multi-form mode) and could replace
              // the list without the newly created item.
              inputs.data = [{ ...res }, ...(Array.isArray(inputs.data) ? inputs.data : [])]
              inputs.dataLoaded = true
              setTimeout(() => {
                targetGroup.get(inputs.dbFeildName)?.patchValue(res.itemId);
                // Fill dependent 'patch' fields (Unit, Item No, Remarks...) for THIS row only
                fireSelectChange(res);
              }, 0);
            })
          } else if (inputs.customAttribute == UICMasterType.TrafficPersonForm) {
            let formData = this.UICFormComponent.gFormGroup.value;

            let indData: IndividualMasterCreateRequestDTO = {
              tenantId: this.User.companyId,
              tenantBranchId: this.User.loggedInBranchId,
              iName: formData.iName,
              gender: formData.gender ?? '',
              aadharNumber: formData.aadharNumber,
              iType: 'TrafficPerson',
              actionUser: this.User.userId.toString()
            }

            this.indvidualMasterService.IndividualMaster_create(indData).subscribe((indRes) => {
              let relData: IndividualRelationCreateRequestDTO = {
                tenantId: this.User.companyId,
                tenantBranchId: this.User.loggedInBranchId,
                indId: indRes.indId,
                relationType: 'TrafficPerson',
                actionUser: this.User.userId.toString()
              }

              let contData: ContactDetailCreateRequestDTO = {
                masterId: indRes.indId,
                masterType: 'Individual',
                personName: indRes.iName,
                designation: formData.designation,
                phone: formData.phone,
                mobile: formData.mobile,
                emailId: formData.emailId,
                contactStatus: 1,
                actionUser: this.User.userId.toString(),
                isDefault: 0,
                tenantId: this.User.companyId,
                tenantBranchId: this.User.loggedInBranchId
              }

              this.individualRelationService.IndividualRelation_create(relData).pipe(takeUntil(this.destroy$)).subscribe((res) => {
                console.log('Individual Realtion Created');

              })

              this.contactDetailService.ContactDetail_create(contData).pipe(takeUntil(this.destroy$)).subscribe((res) => {
                inputs.data = [];
                inputs.data.push(indRes)

                targetGroup.get(inputs.dbFeildName)?.patchValue(indRes.indId)
                this.AddModalRef.close();
              })
            })
          } else if (inputs.customAttribute == UICMasterType.BrokerMasterForm) {
            let formData = this.UICFormComponent.gFormGroup.value;

            let cData: CompanyMasterCreateRequestDTO = {
              cName: formData.cName,
              cCode: formData.cCode,
              gstNo: formData.gstNo,
              panNo: formData.panNo,
              msmeCategory: formData.msmeCategory,
              msmeNumber: formData.msmeNumber,
              actionUser: this.User.userId.toString(),
              cDesc: formData.cDesc ?? '',
              cAddress: formData.cAddress ?? '',
              email: formData.email ?? '',
              phone: formData.phone ?? '',
              website: formData.website ?? '',
              category: formData.category ?? '',
              subCategory: formData.subCategory ?? '',
              contactPerson: formData.contactPerson ?? '',
              contactPersonDesignation: formData.contactPersonDesignation ?? '',
              cType: formData.cType ?? '',
              otherDetail1: formData.otherDetail1 ?? '',
              otherDetail2: formData.otherDetail2 ?? '',
              otherDetail3: formData.otherDetail3 ?? '',
              otherDetail4: formData.otherDetail4 ?? '',
              otherDetail5: formData.otherDetail5 ?? '',
              otherDetail6: formData.otherDetail6 ?? '',
              IsDefault: 0,
              serverId: formData.serverId || undefined
            }

            this.companyMasterService.CompanyMaster_create(cData).subscribe((cRes) => {
              let relData: CompanyRelationCreateRequestDTO = {
                tenantId: this.User.companyId,
                companyId: cRes.tenantId,
                relationType: 'Broker',
                otherDetail1: formData.otherDetail1,
                otherDetail2: formData.otherDetail2,
                actionUser: this.User.userId.toString(),
                isAMCClient: formData.isAMCClient,
                tenantBranchId: this.User.loggedInBranchId,
                gstNo: formData.gstNo ?? '',
                currency: formData.currency ?? '',
                creditLimit: formData.creditLimit ?? 0,
                creditPeriod: formData.creditPeriod ?? '',
                otherDetail3: formData.otherDetail3 ?? '',
                otherDetail4: formData.otherDetail4 ?? '',
                otherDetail5: formData.otherDetail5 ?? '',
                otherDetail6: formData.otherDetail6 ?? '',
                otherDetail7: formData.otherDetail7 ?? '',
                otherDetail8: formData.otherDetail8 ?? '',
                otherDetail9: formData.otherDetail9 ?? '',
                otherDetail10: formData.otherDetail10 ?? '',
                otherDetail11: formData.otherDetail11 ?? '',
                otherDetail12: formData.otherDetail12 ?? '',
                otherDetail13: formData.otherDetail13 ?? '',
                otherDetail14: formData.otherDetail14 ?? '',
                otherDetail15: formData.otherDetail15 ?? '',
                transportId: ensureNumber(formData.transportId) ?? 0,
                agentId: ensureNumber(formData.agentId) ?? 0,
                salesPersonId: ensureNumber(formData.salesPersonId) ?? 0,
                stateCode: formData.stateCode ?? '',
                cName: formData.cName ?? '',
                cCode: formData.cCode ?? '',
                cDesc: formData.cDesc ?? '',
                cAddress: formData.cAddress ?? '',
                email: formData.email ?? '',
                phone: formData.phone ?? '',
                website: formData.website ?? '',
                category: formData.category ?? '',
                subCategory: formData.subCategory ?? '',
                contactPerson: formData.contactPerson ?? '',
                cType: formData.cType ?? '',
                isDefault: 0,
                contactPersonDesignation: formData.contactPersonDesignation ?? '',
                panNo: formData.panNo ?? '',
                msmeCategory: formData.msmeCategory ?? '',
                msmeNumber: formData.msmeNumber ?? ''
              }

              let addData: AddressDetailCreateRequestDTO = {
                masterId: cRes.tenantId,
                masterType: 'Company',
                addressType: '',
                address: formData.address,
                city: formData.city,
                state: formData.state,
                pincode: formData.pincode,
                status: 1,
                actionUser: this.User.userId.toString(),
                isDefault: 0,
                gstNo: formData.gstNo || '',
                otherDetail1: formData.otherDetail1 || '',
                otherDetail2: formData.otherDetail2 || '',
                otherDetail3: formData.otherDetail3 || '',
                otherDetail4: formData.otherDetail4 || '',
                otherDetail5: formData.otherDetail5 || '',
                otherDetail6: formData.otherDetail6 || '',
                tenantId: this.User.companyId,
                tenantBranchId: this.User.loggedInBranchId,
                country: formData.country ?? ''
              }

              let contData: ContactDetailCreateRequestDTO = {
                masterId: cRes.tenantId,
                masterType: 'Company',
                personName: formData.personName,
                mobile: formData.mobile,
                phone: formData.phone,
                contactStatus: 1,
                actionUser: this.User.userId.toString(),
                isDefault: 0,
                tenantId: this.User.companyId,
                tenantBranchId: this.User.loggedInBranchId
              }

              // Parallel execution
              forkJoin({
                relation: this.companyRelationService.CompanyRelation_create(relData),
                address: this.addressDetailService.AddressDetail_create(addData),
                contact: this.contactDetailService.ContactDetail_create(contData)
              }).pipe(takeUntil(this.destroy$)).subscribe({
                next: (response) => {
                  console.log('Company Relation Successfully Created', response.relation);
                  console.log('Address Details Successfully Created', response.address);
                  console.log('Contact Details Successfully Created', response.contact);

                  inputs.data = [];
                  inputs.data.push(cRes);

                  targetGroup.get(inputs.dbFeildName)?.patchValue(cRes.tenantId);
                  this.AddModalRef.close();
                },
                error: (err) => {
                  console.error('Error occurred during creation:', err);
                }
              });
            })
          } else if (inputs.customAttribute == UICMasterType.BillingPartyForm) {

            /* ---------------------------------- INCOMPLETE ----------------------------- */

            let formData = this.UICFormComponent.gFormGroup.value;

            let cData: CompanyMasterCreateRequestDTO = {
              cName: formData.cName,
              cType: formData.cType,
              cCode: formData.cCode,
              panNo: formData.panNo,
              gstNo: formData.gstNo,
              email: formData.email,
              phone: formData.phone,
              website: formData.website,
              cAddress: formData.city + ' ' + formData.state,
              otherDetail1: formData.otherDetail1,
              otherDetail2: formData.otherDetail2,
              actionUser: this.User.userId.toString(),
              cDesc: formData.cDesc,
              category: formData.category,
              subCategory: formData.subCategory,
              contactPerson: formData.contactPerson,
              contactPersonDesignation: formData.contactPersonDesignation,
              msmeCategory: formData.msmeCategory,
              msmeNumber: formData.msmeNumber,
              otherDetail3: formData.otherDetail3,
              otherDetail4: formData.otherDetail4,
              otherDetail5: formData.otherDetail5,
              otherDetail6: formData.otherDetail6,
              IsDefault: formData.IsDefault ?? 0,
              serverId: formData.serverId || undefined
            }

            this.companyMasterService.CompanyMaster_create(cData).pipe(takeUntil(this.destroy$)).subscribe((cRes) => {
              let relData: CompanyRelationCreateRequestDTO = {
                tenantId: this.User.companyId,
                companyId: cRes.tenantId,
                relationType: 'Customer',
                actionUser: this.User.userId.toString(),
                isAMCClient: 0,
                tenantBranchId: this.User.loggedInBranchId,
                gstNo: cRes.gstNo ?? '',
                currency: formData.currency ?? '',
                creditLimit: formData.creditLimit ?? 0,
                creditPeriod: formData.creditPeriod ?? '',
                otherDetail1: formData.otherDetail1 ?? '',
                otherDetail2: formData.otherDetail2 ?? '',
                otherDetail3: formData.otherDetail3 ?? '',
                otherDetail4: formData.otherDetail4 ?? '',
                otherDetail5: formData.otherDetail5 ?? '',
                otherDetail6: formData.otherDetail6 ?? '',
                otherDetail7: formData.otherDetail7 ?? '',
                otherDetail8: formData.otherDetail8 ?? '',
                otherDetail9: formData.otherDetail9 ?? '',
                otherDetail10: formData.otherDetail10 ?? '',
                otherDetail11: formData.otherDetail11 ?? '',
                otherDetail12: formData.otherDetail12 ?? '',
                otherDetail13: formData.otherDetail13 ?? '',
                otherDetail14: formData.otherDetail14 ?? '',
                otherDetail15: formData.otherDetail15 ?? '',
                stateCode: formData.stateCode ?? '',
                cName: formData.cName ?? '',
                cCode: formData.cCode ?? '',
                cDesc: formData.cDesc ?? '',
                cAddress: formData.cAddress ?? '',
                email: formData.email ?? '',
                phone: formData.phone ?? '',
                website: formData.website ?? '',
                category: formData.category ?? '',
                subCategory: formData.subCategory ?? '',
                contactPerson: formData.contactPerson ?? '',
                cType: formData.cType ?? '',
                isDefault: formData.isDefault ?? 0,
                contactPersonDesignation: formData.contactPersonDesignation ?? '',
                panNo: formData.panNo ?? '',
                msmeCategory: formData.msmeCategory ?? '',
                msmeNumber: formData.msmeNumber ?? ''
              }

              let addData: AddressDetailCreateRequestDTO = {
                masterId: cRes.tenantId,
                masterType: 'Company',
                addressType: formData.addressType,
                address: formData.address,
                city: formData.city,
                state: formData.state,
                pincode: formData.pincode,
                status: 0,
                actionUser: this.User.userId.toString(),
                isDefault: 0,
                gstNo: formData.gstNo || '',
                otherDetail1: formData.otherDetail1 || '',
                otherDetail2: formData.otherDetail2 || '',
                otherDetail3: formData.otherDetail3 || '',
                otherDetail4: formData.otherDetail4 || '',
                otherDetail5: formData.otherDetail5 || '',
                otherDetail6: formData.otherDetail6 || '',
                tenantId: this.User.companyId,
                tenantBranchId: this.User.loggedInBranchId,
                country: formData.country ?? ''
              }

              if (this.UICFormComponent.contactDetails.length > 0) {

                this.UICFormComponent.contactDetails = this.UICFormComponent.contactDetails.map(i => ({
                  ...i,
                  masterId: cRes.tenantId,
                  masterType: 'Company'
                }));

                const apiCalls = this.UICFormComponent.contactDetails.map(contact => {
                  const data: ContactDetailCreateRequestDTO = {
                    ...contact,
                    actionUser: this.User.userId.toString()
                  };
                  return this.contactDetailService.ContactDetail_create(data);
                });


                forkJoin(apiCalls).pipe(takeUntil(this.destroy$)).subscribe({
                  next: results => {
                    console.log('Branch created successfully');
                  },
                  error: err => {
                    console.log('Branch created successfully');
                  }
                });
              } else {
                console.log('Branch created successfully');
              }

              forkJoin({
                relation: this.companyRelationService.CompanyRelation_create(relData),
                address: this.addressDetailService.AddressDetail_create(addData)
              }).pipe(takeUntil(this.destroy$)).subscribe((res) => {
                inputs.data = [];
                inputs.data.push(cRes);

                targetGroup.get(inputs.dbFeildName)?.patchValue(cRes.tenantId);
                this.AddModalRef.close();
              });

              fireSelectChange(cRes);
            })
          } else if (inputs.customAttribute == UICMasterType.DestinationMasterForm) {
            let formData = this.UICFormComponent.gFormGroup.value;

            let data: DestinationMasterCreateRequestDTO = {
              tenantId: this.User.companyId,
              tenantBranchId: this.User.loggedInBranchId,
              placeName: formData.placeName,
              shortName: formData.shortName ?? '',
              branchName: formData.branchName ?? '',
              placeCode: formData.placeCode ?? '',
              state: formData.state ?? '',
              pinCode: formData.pinCode ?? '',
              blocked: formData.blocked ?? 0,
              blockedDate: new Date(formData.blockedDate),
              blockedBy: formData.blockedBy ?? '',
              actionUser: this.User.userId.toString()
            }

            this.destinationMasterService.DestinationMaster_Create(data).pipe(takeUntil(this.destroy$)).subscribe((res) => {
              inputs.data = [];
              inputs.data.push(res);

              targetGroup.get(inputs.dbFeildName)?.patchValue(res.destinationId);
              this.AddModalRef.close();
            })

          } else if (inputs.customAttribute == UICMasterType.DriverMasterForm) {

            let formData = this.UICFormComponent.gFormGroup.value;

            let indData: IndividualMasterCreateRequestDTO = {
              tenantId: this.User.companyId,
              tenantBranchId: this.User.loggedInBranchId,
              iName: formData.iName,
              iCode: formData.iCode,
              dateOfBirth: formData.dateOfBirth,
              dateOfJoining: formData.dateOfJoining,
              iType: 'Driver',
              aadharNumber: formData.aadharNumber,
              actionUser: this.User.userId.toString()
            }

            this.indvidualMasterService.IndividualMaster_create(indData).pipe(takeUntil(this.destroy$)).subscribe((indRes) => {

              let relData: IndividualRelationCreateRequestDTO = {
                tenantId: this.User.companyId,
                tenantBranchId: this.User.loggedInBranchId,
                indId: indRes.indId,
                relationType: 'Driver',
                referenceType: formData.referenceType,
                referredBy: formData.referredBy,
                referredByContact: formData.referredByContact,
                actionUser: this.User.userId.toString()
              }

              let contData: ContactDetailCreateRequestDTO = {
                masterId: indRes.indId,
                masterType: 'Individual',
                mobile: formData.mobile,
                phone: formData.phone,
                contactStatus: 1,
                actionUser: this.User.userId.toString(),
                isDefault: 0,
                tenantId: this.User.companyId,
                tenantBranchId: this.User.loggedInBranchId
              }

              let addData: AddressDetailCreateRequestDTO = {
                masterId: indRes.indId,
                masterType: 'Individual',
                address: formData.address,
                state: formData.state,
                city: formData.city,
                pincode: formData.pincode,
                addressType: '',
                status: 1,
                actionUser: this.User.userId.toString(),
                isDefault: 0,
                gstNo: formData.gstNo || '',
                otherDetail1: formData.otherDetail1 || '',
                otherDetail2: formData.otherDetail2 || '',
                otherDetail3: formData.otherDetail3 || '',
                otherDetail4: formData.otherDetail4 || '',
                otherDetail5: formData.otherDetail5 || '',
                otherDetail6: formData.otherDetail6 || '',
                tenantId: this.User.companyId,
                tenantBranchId: this.User.loggedInBranchId,
                country: formData.country ?? ''
              }

              let docData: DocMasterCreateRequestDTO = {
                tenantId: this.User.companyId,
                tenantBranchId: this.User.loggedInBranchId,
                masterId: indRes.indId,
                masterType: 'Individual',
                docName: 'License No',
                docDesc: formData.docDesc ?? '',
                docType: 'Driver License',
                otherDetail1: formData.otherDetail1,
                issuedDate: new Date(formData.issuedDate),
                expiryDate: new Date(formData.expiryDate),
                actionUser: this.User.userId.toString()
              }

              forkJoin({
                relation: this.individualRelationService.IndividualRelation_create(relData),
                address: this.addressDetailService.AddressDetail_create(addData),
                contact: this.contactDetailService.ContactDetail_create(contData),
                document: this.docMasterService.DocMaster_Create(docData)
              }).pipe(takeUntil(this.destroy$)).subscribe({
                next: (res) => {
                  inputs.data = [];

                  inputs.data.push(indRes);

                  targetGroup.get(inputs.dbFeildName)?.patchValue(indRes.indId);
                  this.AddModalRef.close();
                },
                error: (err) => {
                  console.error('Error occurred during creation:', err);
                }
              })

            })
          } else if (inputs.customAttribute == UICMasterType.AddressDetailForm) {
            let formData = this.UICFormComponent.gFormGroup.value;

            const masterId = inputs.dependentOn
              ? targetGroup.get(inputs.dependentOn)?.value ?? 0
              : 0;

            let data: AddressDetailCreateRequestDTO = {
              masterId: masterId,
              masterType: inputs.defaultValue ?? '', // You might want to pass masterType via otherDetails
              addressType: formData.addressType ?? '',
              address: formData.address ?? '',
              city: formData.city ?? '',
              state: formData.state ?? '',
              country: formData.country ?? '',
              pincode: formData.pincode ?? '',
              status: formData.status ?? 1,
              gstNo: formData.gstNo || '',
              otherDetail1: formData.otherDetail1 || '',
              otherDetail2: formData.otherDetail2 || '',
              otherDetail3: formData.otherDetail3 || '',
              otherDetail4: formData.otherDetail4 || '',
              otherDetail5: formData.otherDetail5 || '',
              otherDetail6: formData.otherDetail6 || '',
              actionUser: this.User.userId.toString(),
              isDefault: formData.isDefault ?? 0,
              tenantId: this.User.companyId,
              tenantBranchId: this.User.loggedInBranchId
            };
            this.addressDetailService.AddressDetail_create(data).pipe(takeUntil(this.destroy$)).subscribe((res) => {
              console.log('Address Detail created successfully', res);

              // Add new address to existing dropdown data instead of replacing
              if (!inputs.data) {
                inputs.data = [];
              }
              inputs.data.push(res);

              targetGroup.get(inputs.dbFeildName)?.patchValue(res.addressId)
              this.AddModalRef.close();

              // Trigger onSelectChange to handle dependent fields if any
              fireSelectChange(res);
            });
          }
        }
      })
      this.UICFormComponent.onCloseEvent.subscribe(res => {
        this.AddModalRef.close()
      });
      this.AddModalRef.closed.subscribe(res => {
        this.UICFormComponent.onSubmitEvent.unsubscribe()
        this.UICFormComponent.onCloseEvent.unsubscribe()
        this.isModalOpen = false;
        inputs.searchTerm = ''
      });
      this.AddModalRef.dismissed.subscribe(() => {
        this.isModalOpen = false; // Remove blur when modal is dismissed
        this.UICFormComponent.onSubmitEvent.unsubscribe();
        this.UICFormComponent.onCloseEvent.unsubscribe();
        inputs.searchTerm = '';
      });
    })



  }

  getUICForm(masterType: string) {

  }

  patchValueToFormGroup(rowData: any) {
    Object.keys(rowData).forEach(key => {
      const lowerKey = key.toLowerCase();

      // Find matching control by case-insensitive comparison
      const matchingControlKey = Object.keys(this.gFormGroup.controls)
        .find(controlName => controlName.toLowerCase() === lowerKey);

      // If found, patch the value
      if (matchingControlKey) {
        this.gFormGroup.get(matchingControlKey)?.setValue(rowData[key], { emitEvent: false });
      }

      // Open tax toggle if tax amount or gst rate is non-zero
      if ((lowerKey === 'tax' || lowerKey === 'gstrate') && Number(rowData[key]) > 0) {
        this.showTax = true;
      }

      // Open discount toggle if discount is non-zero
      if (lowerKey === 'discount' && Number(rowData[key]) > 0) {
        this.showDiscount = true;
      }
    });
  }

  onSearchSelect(event: any, uicForm: GenericFormUICDTO) {
    if (event) {
      // If event is an object (selected from dropdown), patch all values and handle dependents
      if (typeof event === 'object' && !Array.isArray(event)) {
        // this.patchValueToFormGroup(event);
        this.onSelectChange(event, uicForm);
      }
    } else {
      // If event is null/undefined, user typed something that's not in the list
      // Handle typed text for dependent fields
      // if (uicForm.searchTerm && uicForm.dependentFields && uicForm.dependentFields.length > 0) {
      //   this.handleTypedTextForDependents(uicForm, uicForm.searchTerm);
      // }
    }
  }

  // handleTypedTextForDependents(parentField: GenericFormUICDTO, typedText: string) {
  //   if (!parentField.dependentFields || parentField.dependentFields.length === 0) return;

  //   // Get parent field metadata to determine which dependent gets what
  //   const parentValueProperty = parentField.patchPropertyName?.trim().toLowerCase() || 'key';
  //   const parentDisplayProperty = parentField.displayPropertyName?.trim().toLowerCase() || 'value';

  //   console.log('Parent field metadata:', {
  //     dbFieldName: parentField.dbFeildName,
  //     patchPropertyName: parentValueProperty,
  //     displayPropertyName: parentDisplayProperty,
  //     typedText
  //   });

  //   parentField.dependentFields.forEach(dependent => {
  //     if (dependent.dataFetchType?.toLowerCase() === 'patch' && dependent.patchPropertyName) {
  //       const dependentPatchProperty = dependent.patchPropertyName.trim().toLowerCase();

  //       // If dependent is trying to patch from the parent's VALUE property (ID/key field)
  //       // Then set it to 0 or null since no item was selected
  //       if (dependentPatchProperty === parentValueProperty) {
  //         const defaultValue = dependent.formDataType?.toLowerCase() === 'number' ? 0 : null;
  //         this.gFormGroup.get(dependent.dbFeildName)?.patchValue(defaultValue);
  //         console.log(`  → ${dependent.dbFeildName} = ${defaultValue} (matches patchPropertyName)`);
  //       }
  //       // If dependent is trying to patch from the parent's DISPLAY property (name/label field)
  //       // Then patch the typed text
  //       else if (dependentPatchProperty === parentDisplayProperty) {
  //         this.gFormGroup.get(dependent.dbFeildName)?.patchValue(typedText);
  //         console.log(`  → ${dependent.dbFeildName} = "${typedText}" (matches displayPropertyName)`);
  //       }
  //       // For other cases, set to null or default
  //       else {
  //         const defaultValue = dependent.formDataType?.toLowerCase() === 'number' ? 0 : null;
  //         this.gFormGroup.get(dependent.dbFeildName)?.patchValue(defaultValue);
  //         console.log(`  → ${dependent.dbFeildName} = ${defaultValue} (no match, using default)`);
  //       }
  //     } 
  //     // If dataFetchType is not 'patch', leave it alone or set to default
  //     else {
  //       console.log(`  → ${dependent.dbFeildName} skipped (dataFetchType: ${dependent.dataFetchType})`);
  //     }
  //   });

  //   console.log(`✓ Typed text handling complete for "${typedText}"`);
  // }

  /**
   * ✨ REFACTORED: Select change handler using DataFetchService
   * Handles dropdown selection and dependent field updates
   */
  onSelectChange(event: any, inputs: GenericFormUICDTO) {
    // ✅ Use refactored version if flag is enabled
    if (this.useRefactoredImplementation) {
      this.onSelectChangeRefactored(event, inputs);
      return;
    }

    // 📝 LEGACY implementation

    if (inputs.dependentFields && inputs.dependentFields.length > 0) {
      this.resetDependentValues(inputs.dbFeildName!, this.gFormGroup, this.gFormUIC);

      inputs.dependentFields.forEach(dependent => {
        if (dependent.dataFetchType!.toLowerCase() == 'api') {
          let searhTerm = this.gFormGroup.get(inputs.dbFeildName)?.value

          dependent.isLoading = true;
          this.http.get(this.formatAPIURL(dependent, searhTerm)).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
            this.gFormUIC = this.gFormUIC.map(i => {
              if (i.dbFeildName.trim().toLowerCase() == dependent.dbFeildName.trim().toLowerCase()) {
                i.data = [];
                i.data = res.items
              }
              return i
            })
            dependent.isLoading = false;
          })
        } else

          if (dependent.dataFetchType!.toLowerCase() == 'patch') {
            let data = { ...event };


            if (dependent.dependentOnKey) {

              let formValue;
              if (dependent.dependentOnKey && dependent.dependentOnKey.trim() != '') {
                formValue = this.getObjectValueCaseInsensitive(data, dependent.dependentOnKey);
              }

              const isDateDependent = (dependent.uiInputType || '').toLowerCase() === 'date'
                || (dependent.formDataType || '').toLowerCase() === 'date';
              if (isDateDependent && formValue) {
                formValue = formatDateForInput(formValue);
              }

              this.gFormGroup.get(dependent.dbFeildName)?.patchValue(formValue);

            } else if ((dependent.patchPropertyName ?? '').trim() !== '') {

              let value: any = null;
              const matchedKey = Object.keys(data).find(
                key => key.toLowerCase() === dependent.patchPropertyName!.trim().toLowerCase()
              );

              if (matchedKey) {
                value = data[matchedKey];

                if (dependent.validationPattern && dependent.validationPattern.trim().toLowerCase() == 'lessthanequaltoself') {
                  this.gFormGroup.get(dependent.dbFeildName)?.setValidators(Validators.max(value));
                  this.gFormGroup.get(dependent.dbFeildName)?.updateValueAndValidity();
                }
              }

              this.gFormGroup.get(dependent.dbFeildName)?.patchValue(value);
            } else {
              let value = data[this.gFormGroup.get(inputs.dbFeildName)!.value];
              this.gFormGroup.get(dependent.dbFeildName)?.patchValue(value)
            }
          }
          else if (dependent.dataFetchType!.toLowerCase() == 'apinpatch' && this.formatAPIURL(dependent, this.gFormGroup.get(inputs.dbFeildName)?.value)) {
            let searchTerm = this.gFormGroup.get(inputs.dbFeildName)?.value
            dependent.isLoading = true;
            this.http.get(this.formatAPIURL(dependent, searchTerm))
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (res: any) => {

                  const first = Array.isArray(res?.items)
                    ? res.items[0]
                    : Array.isArray(res)
                      ? res[0]
                      : res;
                  let value: any = null;

                  const patchKey = (dependent.patchPropertyName ?? '').trim().toLowerCase();
                  if (first) {
                    if (patchKey) {
                      const matchedKey = Object.keys(first).find(
                        k => k.toLowerCase() === patchKey
                      );
                      value = matchedKey ? first[matchedKey] : (first.value ?? first.key ?? null);
                    } else {
                      value = first.value ?? first.key ?? null;
                    }
                  }

                  this.gFormGroup.get(dependent.dbFeildName)?.patchValue(value);
                  dependent.isLoading = false;
                },
                error: () => {
                  dependent.isLoading = false;
                }
              });
          }

        if (dependent.dataFetchType!.toLowerCase() == 'dbquery') {
          const selectedValue = (event && typeof event === 'object' && !Array.isArray(event))
            ? (this.getObjectValueCaseInsensitive(event, inputs.patchPropertyName || 'key')
              ?? this.gFormGroup.get(inputs.dbFeildName)?.value)
            : (event ?? this.gFormGroup.get(inputs.dbFeildName)?.value);
          this.dynamicQuery(selectedValue, dependent, false);
        }
      });
    }
  }

  /**
   * ✅ NEW: Refactored select change using DataFetchService
   */
  onSelectChangeRefactored(event: any, inputs: GenericFormUICDTO) {
    console.log('🎯 [Refactored] onSelectChange:', event, inputs.dbFeildName);

    if (!inputs.dependentFields || inputs.dependentFields.length === 0) {
      return;
    }

    // ✅ Use DataFetchService to handle all dependent fields
    inputs.dependentFields.forEach(dependent => {
      const dependentField = FormField.fromDTO(dependent);

      if (!dependentField.requiresDataFetch) {
        console.log(`  ℹ️ ${dependent.dbFeildName} doesn't require data fetch, skipping`);
        return;
      }

      // ✅ Create context with parent field value
      const context = DataFetchContext.create(
        dependentField,
        this.gFormGroup,
        {
          companyId: this.User.companyId,
          branchId: this.User.loggedInBranchId || 0,
          userId: this.User.userId,
          masterId: this.MasterId,
          masterType: this.gFormUIC[0]?.masterType
        }
      ).withSearchTerm(this.gFormGroup.get(inputs.dbFeildName)?.value);

      dependent.isLoading = true;

      // ✅ Let DataFetchService handle the strategy
      this.dataFetchService.fetchData(context).subscribe({
        next: (data: any) => {
          console.log(`  ✅ Loaded data for dependent field ${dependent.dbFeildName}:`, data);

          // Update the field data in the original array
          this.gFormUIC = this.gFormUIC.map(field => {
            if (field.dbFeildName.trim().toLowerCase() === dependent.dbFeildName.trim().toLowerCase()) {
              field.data = Array.isArray(data) ? data : [data];
              field.isLoading = false;
            }
            return field;
          });

          dependent.isLoading = false;
        },
        error: (err) => {
          console.error(`  ❌ Failed to load dependent field ${dependent.dbFeildName}:`, err);
          dependent.isLoading = false;
        }
      });
    });
  }

  dynamicQuery(event: any, inputs: GenericFormUICDTO, patchValue: boolean = false) {
    if (this.enableMultipleForms) {
      return;
    }

    if (!inputs.dataFetchFrom || inputs.dataFetchFrom.trim() === '') {
      console.warn('⚠️ No SQL query defined for field:', inputs.dbFeildName);
      return;
    }

    // setTimeout(() => {
    console.log("🔍 Dynamic Query Called", inputs.dbFeildName, "Search term:", event?.term ?? event);
    const searchTerm =
      event && typeof event === 'object' && !Array.isArray(event)
        ? (event.term ?? '')
        : (event ?? '');

    const body = {
      dynamicSQL: this.formatAPIURL(inputs, searchTerm)
    };

    const headers = {
      CompanyId: '1',
      BranchId: '1',
      SoftwareId: '1'
    };

    inputs.isLoading = true;

    this.http.post(PrintMaster_DynamicSQLExecutor, body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const dataArray = Array.isArray(res)
            ? res
            : Array.isArray(res?.items)
              ? res.items
              : Array.isArray(res?.data)
                ? res.data
                : [];

          console.log(`✅ Received ${dataArray.length} items for ${inputs.dbFeildName}:`, dataArray);
          inputs.data = dataArray;
          inputs.dataLoaded = true;
          inputs.isLoading = false;
          //this is changing the reference of gFormUIC which is causing the entire form to re-render and losing the current state of dropdowns so added this function 

          const targetField = this.gFormUIC.find(f =>
            f.dbFeildName.trim().toLowerCase() === inputs.dbFeildName.trim().toLowerCase()
          );

          if (targetField) {
            targetField.data = [...dataArray]; // Update data array
            targetField.isLoading = false;
            targetField.dataLoaded = true;
          }
          console.log('✅ Updated gFormUIC field:', this.gFormUIC.find(f =>
            f.dbFeildName.trim().toLowerCase() === inputs.dbFeildName.trim().toLowerCase()
          ));


          // ✅ IMPROVED: Check if we have a preserved value during patching
          const fieldKey = inputs.dbFeildName.toLowerCase();
          const preservedValue = this.patchedValues.get(fieldKey);

          if (this.isPatchingInProgress && preservedValue !== undefined) {
            // ✅ We're in edit mode and have a preserved value - restore it
            console.log(`🔒 Preserving patched value for ${inputs.dbFeildName}:`, preservedValue);

            if (dataArray.length > 0) {
              // ✅ CRITICAL: Always use patchPropertyName (the DB field)
              if (!inputs.patchPropertyName || inputs.patchPropertyName.trim() === '') {
                console.error(`❌ CRITICAL: Missing patchPropertyName for ${inputs.dbFeildName}. Cannot match preserved value!`);
                this.gFormGroup.get(inputs.dbFeildName)?.patchValue(preservedValue, { emitEvent: false });
                return;
              }

              const patchKey = inputs.patchPropertyName.trim();
              console.log(`  ✅ Using patchPropertyName (DB field): ${patchKey}`);

              const matchedItem = dataArray.find((item: any) => {
                const itemValue = this.getObjectValueCaseInsensitive(item, patchKey);
                return itemValue !== undefined && itemValue !== null && `${itemValue}` === `${preservedValue}`;
              });

              if (matchedItem) {
                console.log(`✅ Found matching item for preserved value:`, matchedItem);
                this.gFormGroup.get(inputs.dbFeildName)?.patchValue(preservedValue, { emitEvent: false });
                this.onSelectChange(matchedItem, inputs);
              } else {
                console.warn(`⚠️ Preserved value ${preservedValue} not found in loaded data`);
                this.gFormGroup.get(inputs.dbFeildName)?.patchValue(preservedValue, { emitEvent: false });
              }
            }
          } else if (patchValue && !this.isPatchingInProgress) {
            // ✅ Normal mode (not editing) - patch first value
            if (dataArray.length > 0) {
              // ✅ CRITICAL: Always use patchPropertyName (the DB field)
              if (!inputs.patchPropertyName || inputs.patchPropertyName.trim() === '') {
                console.error(`❌ CRITICAL: Missing patchPropertyName for ${inputs.dbFeildName}. Cannot auto-patch value!`);
                return;
              }

              const patchKey = inputs.patchPropertyName.trim();
              const first = dataArray[0];
              const valueToPatch = this.getObjectValueCaseInsensitive(first, patchKey);

              if (valueToPatch !== undefined && valueToPatch !== null) {
                console.log(`📝 Auto-patching first value for ${inputs.dbFeildName} using ${patchKey}:`, valueToPatch);
                this.gFormGroup.get(inputs.dbFeildName)?.patchValue(valueToPatch, { emitEvent: false });
                this.onSelectChange(first, inputs);
              }
            } else {
              this.gFormGroup.get(inputs.dbFeildName)?.patchValue(null, { emitEvent: false });
            }
          }
        },
        error: (err) => {
          this.gFormUIC = this.gFormUIC.map(field => {
            if (field.dbFeildName.trim().toLowerCase() === inputs.dbFeildName.trim().toLowerCase()) {
              return { ...field, isLoading: false, dataLoaded: false };
            }
            return field;
          });
          console.error('❌ DB Query Error', err);
        }
      });
    // }, 300);
  }


  contactDetails: ContactDetailDTO[] = []
  handleContactDetails(contacts: ContactDetailDTO[]) {
    this.contactDetails = []
    this.contactDetails = contacts
  }
  createContactDetail(masterId: number, masterType: string) {
    this.contactDetails = this.contactDetails.map(i => ({
      ...i,
      masterId,
      masterType
    }));

  }

  addressDetails: AddressDetailDTO[] = []
  handleAddressDetails(addresses: AddressDetailDTO[]) {
    this.addressDetails = []
    this.addressDetails = addresses
  }
  createAddressDetail(masterId: number, masterType: string) {
    this.addressDetails = this.addressDetails.map(i => ({
      ...i,
      masterId,
      masterType
    }));

  }

  documentDetails: DocMasterDTO[] = [];

  handleDocumentDetails(docData: DocMasterDTO[]) {
    this.documentDetails = [];
    this.documentDetails = docData;
  }

  handleAddressUpdate(updatedAddress: AddressDetailDTO, inputs: GenericFormUICDTO) {
    console.log('Address updated:', updatedAddress);
    // Optionally refresh the dropdown data
    if (inputs.data && Array.isArray(inputs.data)) {
      const index = inputs.data.findIndex((addr: any) => addr.addressId === updatedAddress.addressId);
      if (index > -1) {
        inputs.data[index] = updatedAddress;
      }
    }
  }

  handleAddressEditStateChange(isEditing: boolean) {
    this.isAddressInEditMode = isEditing;
  }

  convertToHindi(inputs: GenericFormUICDTO) {
    // if(inputs.otherDetails?.toLowerCase() == 'converttohindi'){

    //   let control = this.gFormGroup.get(inputs.dbFeildName)!
    //   control.setValue(Sanscript.t(control.value, 'itrans', 'devanagari'))
    // }
  }

  // Simple comparator for ng-select when bindValue and selected are primitives
  compareByValue(item: any, selected: any): boolean {
    return String(item) === String(selected);
  }

  private getObjectValueCaseInsensitive(source: any, keyName: string): any {
    if (!source || !keyName) {
      return undefined;
    }

    const matchedKey = Object.keys(source).find(
      key => key.toLowerCase() === keyName.trim().toLowerCase()
    );

    return matchedKey ? source[matchedKey] : undefined;
  }

  getCompareFn(inputs: any) {
    return (item: any, selected: any): boolean => {
      if (!item || selected == null) return false;

      const key = inputs?.patchPropertyName || 'key';
      const itemValue = item[key];

      // ✅ Compare directly with the selected value
      return itemValue?.toString() === selected?.toString();
    };
  }

  /**
   * ng-select's own default [searchFn] re-filters [items] on the client
   * with a plain case-insensitive "does bindLabel contain the whole typed
   * string as one continuous substring" check — completely independent of
   * whatever server-side search already ran.
   *
   * For 'api' and 'dbquery' fields, every keystroke already triggers a
   * fresh server call (onSearch/onSearchForInstance/dynamicQuery/
   * dynamicQueryForInstance) and `inputs.data` is replaced with the
   * server's already-correct, order-independent, multi-token result for
   * exactly what the user typed. ng-select's built-in filter then runs
   * AGAIN on top of that and hides rows whose display text doesn't
   * literally contain the raw search string in one piece — e.g. "TRANSFORMER
   * OIL SUPPLY" never contains "trans supp" as a continuous substring
   * (the word "OIL" sits in between), so a perfectly correct server result
   * gets hidden and the dropdown shows "No items found" even though the
   * API returned the right rows. Returning `true` unconditionally for
   * these fields tells ng-select "trust what the server gave you, don't
   * re-filter it".
   *
   * ValueList fields load their full option list once and are searched
   * entirely client-side (no per-keystroke server round trip), so
   * ng-select's own default matching is correct there — returning
   * `undefined` leaves that default behavior untouched.
   */
  getSearchFn(inputs: any): ((term: string, item: any) => boolean) | undefined {
    const type = (inputs?.dataFetchType || '').toString().toLowerCase();
    if (type === 'api' || type === 'dbquery') {
      return () => true;
    }
    return undefined;
  }


  getAddTagFn(inputs: any): AddTagFn {
    // This outer function is called during template binding (may run multiple times)
    // Only return the function reference, don't execute heavy logic here

    return (term: string) => {
      // This inner function ONLY runs when user actually uses addTag feature
      console.log('AddTag triggered!');
      console.log('Field:', inputs.dbFeildName);
      console.log('Term:', term);

      if (inputs.data.length == 0) {

        let PatchPropertyDefaultValue = this.gFormUIC.find(f => f.dbFeildName === inputs.patchPropertyName)?.formDataType?.toLowerCase() === 'number' ? 0 : '';
        this.gFormGroup.get(inputs.patchPropertyName)?.patchValue(PatchPropertyDefaultValue, { emitEvent: false });
      } // Clear existing data to avoid duplicates

      // ✅ FIRST: Clear all dependent fields to remove old data
      if (inputs.dependentFields && inputs.dependentFields.length > 0) {
        inputs.dependentFields.forEach((depField: any) => {
          const defaultValue = depField.formDataType?.toLowerCase() === 'number' ? 0 : null;
          this.gFormGroup.get(depField.dbFeildName)?.patchValue(defaultValue, { emitEvent: false });
          console.log(`  → Cleared ${depField.dbFeildName}`);
        });
      }

      // Process patchDataInto only when addTag is actually used
      if (inputs.patchDataInto && inputs.patchDataInto.trim() !== '') {
        let patchingArray = inputs.patchDataInto.split(',').map((s: string) => s.trim());
        console.log('Patching to fields:', patchingArray);

        // Patch the typed text to all fields in patchingArray
        // patchingArray.forEach((fieldName: string) => {
        //   if (this.gFormGroup.get(fieldName)) {
        //     this.gFormGroup.get(fieldName)?.patchValue(term, { emitEvent: false });
        //     console.log(`  → ${fieldName} = "${term}"`);
        //   }
        // });
        patchingArray.forEach((fieldName: string) => {
          if (this.gFormGroup.get(fieldName)) {
            // Find the field config to get its formDataType
            const fieldConfig = this.gFormUIC.find(f => f.dbFeildName === fieldName);
            const defaultValue = fieldConfig?.formDataType?.toLowerCase() === 'number' ? 0 : '';
            this.gFormGroup.get(fieldName)?.patchValue(defaultValue, { emitEvent: false });
            console.log(`  → Reset ${fieldName} to ${defaultValue}`);
          }
        });
      }

      // Handle dependent fields with the typed text
      // this.handleTypedTextForDependents(inputs, term);

      // Reset the parent field to 0/null (since it should hold an ID, not text)
      const defaultValue = inputs.formDataType?.toLowerCase() === 'number' ? 0 : null;
      this.gFormGroup.get(inputs.dbFeildName)?.patchValue(defaultValue, { emitEvent: false });

      return term;
    };
  }




  // handleAddressChange(event: {billing: number, delivery: number}, inputs: any) {
  //   if (inputs.patchDataInto) {
  //     const fields = inputs.patchDataInto.split(',').map((f: string) => f.trim());
  //     const billingField = fields[0];
  //     const deliveryField = fields[1];

  //     if (billingField && this.gFormGroup.get(billingField)) {
  //       this.gFormGroup.get(billingField)?.patchValue(event.billing);
  //     }

  //     if (deliveryField && this.gFormGroup.get(deliveryField)) {
  //       this.gFormGroup.get(deliveryField)?.patchValue(event.delivery);
  //     }
  //   }
  // }
  handleAddressChange(event: { billing: number, delivery: number }, inputs: any) {
    if (inputs.patchDataInto) {
      const fields = inputs.patchDataInto.split(',').map((f: string) => f.trim());
      const billingField = fields[0];
      const deliveryField = fields[1];

      const billingFieldDatatype = this.gFormUIC.find(f => f.dbFeildName === billingField)?.formDataType

      if (billingField && this.gFormGroup.get(billingField)) {
        let EBilling: number | string;
        if (billingFieldDatatype?.toLowerCase().trim() === 'string') {
          EBilling = String(event.billing);
        }
        else if (billingFieldDatatype?.toLowerCase().trim() === 'number') {
          EBilling = Number(event.billing);
        }
        else {
          EBilling = event.billing; // fallback
        }

        this.gFormGroup.get(billingField)?.patchValue(EBilling);
      }

      if (deliveryField && this.gFormGroup.get(deliveryField)) {
        this.gFormGroup.get(deliveryField)?.patchValue(event.delivery);
      }
    }
  }

  handleBillingAddressChange(billingAddressId: number) {
    this.gFormGroup.get('billingAddressId')?.patchValue(billingAddressId);
  }

  handleDeliveryAddressChange(deliveryAddressId: number) {
    this.gFormGroup.get('deliveryAddressId')?.patchValue(deliveryAddressId);
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  // ============== MULTIPLE FORMS SEARCH METHODS ==============

  // ✅ NEW METHOD: Search for multiple form instances
  onSearchForInstance(
    event: { term: string; items: any[] },
    uicForm: GenericFormUICDTO,
    formGroup: FormGroup,
    formInstance: any
  ) {

    if (uicForm.dataFetchType?.toLowerCase() == 'dbquery') {
      this.dynamicQueryForInstance(event.term, uicForm, formInstance);
      return;
    }

    if (uicForm.dataFetchType?.toLowerCase() == 'valuelist') {
      return;
    }
    uicForm.searchTerm = event.term;
    clearTimeout(this.searchTimeout);

    this.searchTimeout = setTimeout(() => {
      // ✅ Patch the specific form instance's form group
      formGroup.get(uicForm.dbFeildName)?.patchValue(event.term);

      // Find the field in THIS instance's UIC array
      const instanceField = formInstance.formUIC.find(
        (f: GenericFormUICDTO) => f.dbFeildName === uicForm.dbFeildName
      );

      if (instanceField) {
        instanceField.isLoading = true;
      }

      // ✅ Use formatAPIURLForInstance with the correct formGroup
      this.http.get(environment.getApiUrl(this.formatAPIURLForInstance(uicForm, event.term, formGroup)))
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            if (instanceField) {
              instanceField.data = res.items.map((item: any) => {
                const patchedKey = Object.keys(item).find(key =>
                  key.trim().toLowerCase() === uicForm.patchPropertyName.trim().toLowerCase()
                );

                if (patchedKey && uicForm.formDataType?.trim().toLowerCase() === 'string') {
                  return {
                    ...item,
                    [patchedKey]: String(item[patchedKey])
                  };
                }

                return item;
              });

              instanceField.isLoading = false;

              // If no results and dependent fields exist
              if (res.items.length === 0 && instanceField.dependentFields && instanceField.dependentFields.length > 0) {
                // Handle typed text for dependents if needed
              }
            }
          },
          error: (err) => {
            if (instanceField) {
              instanceField.isLoading = false;
            }
          }
        });
    }, 300);
  }

  // ✅ NEW METHOD: Handle search select for multiple form instances
  onSearchSelectForInstance(event: any, uicForm: GenericFormUICDTO, formInstance: any) {
    if (event) {
      // If event is an object (selected from dropdown), handle dependents
      if (typeof event === 'object' && !Array.isArray(event)) {
        this.onSelectChangeForInstance(event, uicForm, formInstance);
      }
    } else {
      // If event is null/undefined, user typed something that's not in the list
      // You can handle typed text for dependent fields here if needed
    }
  }

  // ✅ NEW METHOD: Handle select change for multiple form instances
  onSelectChangeForInstance(event: any, inputs: GenericFormUICDTO, formInstance: any) {
    console.log('onSelectChangeForInstance', event, inputs);

    const formGroup = formInstance.formGroup;
    const formUIC = formInstance.formUIC;

    if (inputs.dependentFields && inputs.dependentFields.length > 0) {
      this.resetDependentValues(inputs.dbFeildName!, formGroup, formUIC);

      inputs.dependentFields.forEach(dependent => {
        // Find the dependent field in THIS instance's UIC array
        const instanceDepField = formUIC.find(
          (f: GenericFormUICDTO) => f.dbFeildName === dependent.dbFeildName
        );

        if (dependent.dataFetchType!.toLowerCase() == 'api') {
          let searchTerm = formGroup.get(inputs.dbFeildName)?.value;

          if (instanceDepField) {
            instanceDepField.isLoading = true;
          }

          // ✅ Use formatAPIURLForInstance with correct formGroup
          this.http.get(environment.getApiUrl(this.formatAPIURLForInstance(dependent, searchTerm, formGroup)))
            .pipe(takeUntil(this.destroy$))
            .subscribe((res: any) => {
              if (instanceDepField) {
                instanceDepField.data = res.items || [];
                instanceDepField.isLoading = false;
              }
            });
        }
        else if (dependent.dataFetchType!.toLowerCase() == 'patch') {
          let data = { ...event };
          console.log("Event data", data);

          if ((dependent.patchPropertyName ?? '').trim() != '') {
            const formValue = formGroup.get(inputs.dbFeildName)!.value?.toString().toLowerCase();
            console.log('FormValues', formValue);

            if (data) {
              let value;
              const matchedKey = Object.keys(data).find(key =>
                key.toLowerCase() === dependent.patchPropertyName.toLowerCase()
              );

              console.log("matchedKey", matchedKey);

              if (matchedKey) {
                value = data[matchedKey];

                const isDateDependent = (dependent.uiInputType || '').toLowerCase() === 'date'
                  || (dependent.formDataType || '').toLowerCase() === 'date';
                if (isDateDependent && value) {
                  value = formatDateForInput(value);
                }

                if (dependent.validationPattern && dependent.validationPattern.trim().toLowerCase() == 'lessthanequaltoself') {
                  formGroup.get(dependent.dbFeildName)?.setValidators(Validators.max(value));
                  formGroup.get(dependent.dbFeildName)?.updateValueAndValidity();
                }
              }

              formGroup.get(dependent.dbFeildName)?.patchValue(value);
            } else {
              formGroup.get(dependent.dbFeildName)?.patchValue(null);
            }
          } else {
            let value = data[formGroup.get(inputs.dbFeildName)!.value];
            formGroup.get(dependent.dbFeildName)?.patchValue(value);
          }
        }
        else if (dependent.dataFetchType!.toLowerCase() == 'apinpatch' && this.formatAPIURLForInstance(dependent, '', formGroup)) {
          if (instanceDepField) {
            instanceDepField.isLoading = true;
          }

          this.http.get(environment.getApiUrl(this.formatAPIURLForInstance(dependent, '', formGroup)))
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res: any) => {
                const first = Array.isArray(res?.items)
                  ? res.items[0]
                  : Array.isArray(res)
                    ? res[0]
                    : res;
                let value: any = null;

                const patchKey = (dependent.patchPropertyName ?? '').trim().toLowerCase();
                if (first) {
                  if (patchKey) {
                    const matchedKey = Object.keys(first).find(
                      k => k.toLowerCase() === patchKey
                    );
                    value = matchedKey ? first[matchedKey] : (first.value ?? first.key ?? null);
                  } else {
                    value = first.value ?? first.key ?? null;
                  }
                }

                formGroup.get(dependent.dbFeildName)?.patchValue(value);

                if (instanceDepField) {
                  instanceDepField.isLoading = false;
                }
              },
              error: () => {
                if (instanceDepField) {
                  instanceDepField.isLoading = false;
                }
              }
            });
        }
        else if (dependent.dataFetchType!.toLowerCase() == 'dbquery') {
          const selectedValue = (event && typeof event === 'object' && !Array.isArray(event))
            ? (this.getObjectValueCaseInsensitive(event, inputs.patchPropertyName || 'key')
              ?? formGroup.get(inputs.dbFeildName)?.value)
            : (event ?? formGroup.get(inputs.dbFeildName)?.value);
          this.dynamicQueryForInstance(selectedValue, dependent, formInstance, false);
        }
      });
    }
  }

  private getResetValue(field: GenericFormUICDTO): any {
    const inputType = (field.uiInputType || '').toLowerCase();
    const dataType = (field.formDataType || '').toLowerCase();

    if (inputType === 'select' || inputType === 'selectnadd' || inputType === 'searchnadd') {
      return null;
    }

    if (dataType === 'string') {
      return '';
    }

    return null;
  }

  private resetDependentValues(
    parentFieldName: string,
    formGroup: FormGroup,
    formUIC: GenericFormUICDTO[],
    visited: Set<string> = new Set<string>()
  ): void {
    const key = (parentFieldName || '').toLowerCase();
    if (!key || visited.has(key)) {
      return;
    }

    visited.add(key);

    const dependents = formUIC.filter(
      field => (field.dependentOn || '').toLowerCase() === key
    );

    dependents.forEach(dependent => {
      formGroup.get(dependent.dbFeildName!)?.patchValue(this.getResetValue(dependent), { emitEvent: false });
      this.resetDependentValues(dependent.dbFeildName!, formGroup, formUIC, visited);
    });
  }

  private resolveDependencyOrder(): GenericFormUICDTO[] {
    const map = new Map<string, GenericFormUICDTO>();
    this.gFormUIC.forEach(f => map.set(f.dbFeildName!.toLowerCase(), f));

    const visited = new Set<string>();
    const result: GenericFormUICDTO[] = [];

    const visit = (field: GenericFormUICDTO) => {
      const key = field.dbFeildName!.toLowerCase();
      if (visited.has(key)) return;
      visited.add(key);

      if (field.dependentOn) {
        const parent = map.get(field.dependentOn.toLowerCase());
        if (parent) visit(parent);
      }

      result.push(field);
    };

    this.gFormUIC.forEach(f => visit(f));
    return result;
  }
  private async patchSingleFormWithDependencies(rowData: any) {
    // ✅ Mark patching as in progress
    this.isPatchingInProgress = true;
    this.isEditMode = true;
    this.patchedValues.clear();
    this.dependencyExecutionTracker.clear();

    this.gFormUIC.forEach(f => {
      if ((f.dependentOn && f.dependentOn.trim() !== '') || f.searchable === 1) {
        f.dataLoaded = false;
        f.isLoading = false;
      }
    });

    const orderedFields = this.resolveDependencyOrder();
    console.log('Ordered fields for patching:', orderedFields.map(f => f.dbFeildName));

    for (const f of orderedFields) {
      try {
      const control = this.gFormGroup.get(f.dbFeildName!);

      if (!control) continue;

      const rowKey = Object.keys(rowData)
        .find(k => k.toLowerCase() === f.dbFeildName!.toLowerCase());

      if (!rowKey) {
        console.log(`⏭️ [patch] ${f.dbFeildName}: no rowKey match in patchData`);
        continue;
      }

      let value = rowData[rowKey];
      const isDateField =
        f.formDataType?.toLowerCase().trim() === 'date' ||
        f.uiInputType?.toLowerCase().trim() === 'date';

      if (isDateField) {

        try {

          // ✅ Skip invalid values
          if (
            value !== undefined &&
            value !== null &&
            value !== '' &&
            !isNaN(new Date(value).getTime())
          ) {

            value = formatDate(
              value,
              'yyyy-MM-dd',
              'en-US'
            );

          } else {

            console.warn(
              `⚠️ Invalid date value for ${f.dbFeildName}:`,
              value
            );

            value = null;
          }

        } catch (dateErr) {

          console.error(
            `❌ Date conversion failed for ${f.dbFeildName}`,
            value,
            dateErr
          );

          value = null;
        }
      }


      // ✅ Store the patched value to preserve it
      this.patchedValues.set(f.dbFeildName!.toLowerCase(), value);
      // 1️⃣ Patch parent field FIRST
      control.setValue(value, { emitEvent: false });
      console.log(`📝 [patch] ${f.dbFeildName} <- "${value}" (rowKey="${rowKey}"), control now =`, control.value);

      // 1.1️⃣ Load parent API / DBQuery options first (important for dependentOnKey-based resolution)
      // ✅ Skip if already loading or loaded
      if (!f.isLoading && !f.dataLoaded) {
        const fetchType = f.dataFetchType?.toLowerCase();
        if (fetchType === 'api' || fetchType === 'dbquery') {
          if (f.searchable === 1 && (!f.dependentOn || !f.dependentOn.trim())
              && value !== null && value !== undefined && `${value}`.trim() !== '') {
            const bindKey = (f.patchPropertyName || 'key').trim();
            const alreadySeeded = Array.isArray(f.data) && f.data.some((item: any) =>
              `${this.getObjectValueCaseInsensitive(item, bindKey)}` === `${value}`
            );
            if (!alreadySeeded) {
              const displayKey = f.displayPropertyName?.trim();
              const rowDisplayKey = displayKey
                ? Object.keys(rowData).find(k => k.toLowerCase() === displayKey.toLowerCase())
                : undefined;
              const seeded: any = { [bindKey]: value };
              if (displayKey) {
                seeded[displayKey] = rowDisplayKey ? rowData[rowDisplayKey] : '';
              }
              f.data = [seeded, ...(Array.isArray(f.data) ? f.data : [])];
            }
          }
        }

        if (fetchType === 'api') {
          await this.loadApiForField(f);
        } else if (fetchType === 'dbquery') {
          await this.loadDbQueryForField(f, value);
        }
      }

      // 1.2️⃣ Load dependent field data (dbquery/api) even when patching with emitEvent:false
      if (f.dependentFields && f.dependentFields.length > 0) {

        // ✅ Ensure parent is FULLY READY before running children
        if (f.dataFetchType?.toLowerCase() === 'api' || f.dataFetchType?.toLowerCase() === 'dbquery') {
          if (!f.dataLoaded) {
            console.log(`⏳ Waiting for parent ${f.dbFeildName} to load before running dependents`);
            await this.waitForFieldToLoad(f);
          }
        }

        for (const dependentFieldRef of f.dependentFields) {

          const dependentField = this.gFormUIC.find(field =>
            (field.dbFeildName || '').trim().toLowerCase() ===
            (dependentFieldRef.dbFeildName || '').trim().toLowerCase()
          ) || dependentFieldRef;

          const dependentKey = dependentField.dbFeildName!.toLowerCase();

          // ✅ 🚫 Prevent multiple execution
          if (this.dependencyExecutionTracker.has(dependentKey) && dependentField.dataLoaded) {
            console.log(`⏭️ Skipping duplicate execution for ${dependentField.dbFeildName}`);
            continue;
          }

          const dependentFetchType = (dependentField.dataFetchType || '').toLowerCase();

          // ✅ Mark as executed BEFORE calling (avoid race condition)

          if (dependentFetchType === 'dbquery' && !dependentField.isLoading && !dependentField.dataLoaded) {
            await this.loadDbQueryForField(dependentField, value);
            this.dependencyExecutionTracker.add(dependentKey);
          }

          if (dependentFetchType === 'api' && !dependentField.isLoading && !dependentField.dataLoaded) {
            await this.loadApiForField(dependentField, value);
          }

          if (dependentFetchType === 'patch') {
            this.patchFromParent(dependentField);
          }
        }
      }

      // 3️⃣ Handle PATCH fields (derived fields)
      if (f.dataFetchType?.toLowerCase() === 'patch' && !rowKey) {
        this.patchFromParent(f);
      }

      // Do NOT run jsexecutors while patching an existing record: auto-fill scripts
      // (e.g. departmentAssign) would overwrite the saved values we just patched from
      // rowData with values derived from the logged-in user / other live queries.
      // Saved data must win on edit. jsexecutors still run on real field changes after
      // patching completes (isPatchingInProgress is cleared a tick later).
      if (f.dataFetchType?.toLowerCase() === 'jsexecutor' && f.dataFetchFrom && !this.isPatchingInProgress) {
        this.executeJavaScript(f);
      }
      } catch (fieldPatchError) {
        // A failure loading/resolving ANY single field (e.g. a dependent dbquery/api
        // call throwing) must not abort patching of every field ordered after it in
        // this tenant's field list - that previously left fields like purchaseOrderId
        // unpatched, silently turning an Update into a Create. Log and keep going.
        console.error(`\u274c [patch] Unexpected error while patching field "${f.dbFeildName}" - continuing with remaining fields`, fieldPatchError);
      }
    }

    // Fields like itemId/indentId are dbquery-typed with an intentionally EMPTY
    // dataFetchFrom: their real SQL is injected at runtime by a hidden sibling
    // jsexecutor (itemIdPatch, indentList) that only rewrites the target field's
    // dataFetchFrom and triggers a query — it never has its own dbFeildName in
    // rowData, so the loop above always skips it via `if (!rowKey) continue`, and
    // it's therefore never reached by the isPatchingInProgress guard directly above.
    // Without this step such fields keep an empty options list forever on edit, so
    // ng-select can't find a matching item to display even though the raw saved
    // value is already patched onto the control. Only "pure" option-provider
    // scripts (no direct .patchValue(/.setValue( on any control) are run here —
    // they merely seed a sibling field's data via dynamicQuery/dynamicQueryForInstance,
    // whose existing preserved-value matching (gated on isPatchingInProgress, still
    // true here) then safely selects the saved value from the freshly loaded options.
    await this.runOptionProviderJsExecutors();

    // Retry once for any dependent select/selectnadd (e.g. itemId, whose real SQL is
    // written at runtime by an option-provider jsexecutor like itemIdPatch) that still
    // has no matching entry for its saved value. On a form with many concurrent
    // dependent chains (multi-instance detail rows, each with their own Category ->
    // SubCategory -> item-list fetch), a sibling API call finishing slightly late can
    // leave runOptionProviderJsExecutors' wait window closing before this row's list
    // has actually arrived, so the row's dropdown renders blank despite the underlying
    // control already holding the correct saved id. Re-running the same (idempotent)
    // option-provider pass gives that late response one more chance to land and match.
    const unresolvedDependentSelects = this.gFormUIC.filter(f => {
      if (!f.dbFeildName) return false;
      const inputType = (f.uiInputType || '').toLowerCase();
      if (inputType !== 'select' && inputType !== 'selectnadd') return false;
      if (!f.dependentOn || !f.dependentOn.trim()) return false;

      const value = this.gFormGroup.get(f.dbFeildName)?.value;
      if (value === null || value === undefined || value === '') return false;

      const bindKey = (f.patchPropertyName || 'key').trim();
      const hasMatch = Array.isArray(f.data) && f.data.some((item: any) =>
        `${this.getObjectValueCaseInsensitive(item, bindKey)}` === `${value}`
      );
      return !hasMatch;
    });

    if (unresolvedDependentSelects.length > 0) {
      console.log('🔁 [retry] Dependent selects still unmatched after first pass, retrying:', unresolvedDependentSelects.map(f => f.dbFeildName));
      await this.runOptionProviderJsExecutors();
    }

    this.reconcileDependentSelectValues(rowData);

    // ✅ REMOVED: executeAllJSExecutorsForCurrentForm() - let JS executors run only on field changes, not during initial patching
    // this.executeAllJSExecutorsForCurrentForm();

    // Auto-open tax/discount toggles (setValue with emitEvent:false suppresses valueChanges)
    const formValues = this.gFormGroup.value;
    let desiredShowTax = this.showTax;
    let desiredShowDiscount = this.showDiscount;
    Object.keys(formValues).forEach(k => {
      const lk = k.trim().toLowerCase();
      if ((lk === 'gstrate' || lk === 'tax') && Number(formValues[k]) > 0) {
        desiredShowTax = true;
      }
      if (lk === 'discount' && Number(formValues[k]) > 0) {
        desiredShowDiscount = true;
      }
    });

    // Force-remount the uic-tax / uic-discount sub-components so they re-read the freshly
    // patched values. We patch with emitEvent:false into the SAME FormGroup reference, so
    // neither their valueChanges nor their ngOnChanges (input ref unchanged) would fire —
    // an already-open toggle would otherwise keep stale values from the previous row/create.
    // Quotation avoids this by assigning a brand-new FormGroup on edit; this gives the
    // shared patch path the same fresh-init behaviour. Toggling off this tick then on the
    // next forces *ngIf to destroy + recreate the components, re-running their reverse-engineer.
    this.showTax = false;
    this.showDiscount = false;
    setTimeout(() => {
      this.showTax = desiredShowTax;
      this.showDiscount = desiredShowDiscount;
    });

    // ✅ Mark patching as complete after a small delay.
    // Awaited (rather than a bare fire-and-forget setTimeout) because patchWithDependencies'
    // multi-instance loop calls this method once per row and awaits each call before
    // reassigning this.gFormGroup/this.gFormUIC to the next row's instance. Without the
    // await, this callback's this.gFormGroup/this.gFormUIC/this.isPatchingInProgress reads
    // below would fire 500ms later against whichever instance happened to be "current" at
    // that moment — i.e. a later row, or none — instead of the row this call is patching.
    await new Promise<void>(resolve => setTimeout(() => {
      // 🔒 Final authoritative re-patch: everything above this point (dependent
      // dbquery/api loads, onSelectChange's resetDependentValues, option-provider
      // jsexecutors) runs async and can clear/overwrite a control after we already
      // set it from rowData earlier in this method (e.g. a vendor/indent dropdown's
      // dependent-field reset wiping "Nalco Rate"/"Payment Terms" on a PO, or
      // departmentAssign wiping "Department"/"Requested By" on an indent). Rather than
      // chase every individual race, re-apply the ORIGINAL API row one last time now
      // that all of that async activity has had time to settle, so the saved data is
      // guaranteed to win regardless of what ran in between.
      this.forceReapplyPatchData(rowData);

      this.isPatchingInProgress = false;
      this.patchedValues.clear();

      // Re-trigger writeValue on all select controls so ng-select re-evaluates the
      // selected label against the now-loaded items array. Needed when items arrived
      // after the initial setValue call (timing) or on a re-patch where items reloaded.
      this.gFormUIC.forEach(f => {
        const inputType = (f.uiInputType || '').toLowerCase();
        if (inputType === 'select' || inputType === 'selectnadd') {
          const control = this.gFormGroup.get(f.dbFeildName!);
          if (control) {
            const val = control.value;
            if (val !== null && val !== undefined) {
              control.setValue(val, { emitEvent: false });
            }
          }
        }
      });
      console.log(this.gFormGroup.value);
      console.log('✅ Patching complete - auto-patch re-enabled');
      resolve();
    }, 500));
  }

  // Re-applies every value present in the original API row directly onto the
  // FormGroup, bypassing all the dependent-field/jsexecutor machinery. Used as the
  // final step of patchSingleFormWithDependencies so the saved record always wins
  // over any dependent dbquery/api/jsexecutor side-effect that ran during patching.
  private forceReapplyPatchData(rowData: any) {
    if (!rowData) return;

    this.gFormUIC.forEach(f => {
      if (!f.dbFeildName) return;

      const control = this.gFormGroup.get(f.dbFeildName);
      if (!control) return;

      const rowKey = Object.keys(rowData).find(k => k.toLowerCase() === f.dbFeildName!.toLowerCase());
      if (!rowKey) return;

      let value = rowData[rowKey];
      const isDateField =
        f.formDataType?.toLowerCase().trim() === 'date' ||
        f.uiInputType?.toLowerCase().trim() === 'date';

      if (isDateField) {
        try {
          if (value !== undefined && value !== null && value !== '' && !isNaN(new Date(value).getTime())) {
            value = formatDate(value, 'yyyy-MM-dd', 'en-US');
          } else {
            value = null;
          }
        } catch {
          value = null;
        }
      }

      control.setValue(value, { emitEvent: false });
    });

    console.log('🔒 [force-repatch] Original saved values re-applied after all async patch side-effects settled');
  }

  private waitForFieldToLoad(
    field: GenericFormUICDTO,
    timeout: number = 5000
  ): Promise<void> {

    return new Promise(resolve => {

      const startedAt = Date.now();

      const check = () => {

        // ✅ Success
        if (field.dataLoaded) {
          resolve();
          return;
        }

        // ✅ Failed / stopped
        if (!field.isLoading) {
          resolve();
          return;
        }

        // ✅ Timeout protection
        if (Date.now() - startedAt > timeout) {

          console.warn(
            `⏰ Timeout waiting for ${field.dbFeildName}`
          );

          field.isLoading = false;

          resolve();

          return;
        }

        setTimeout(check, 50);
      };

      check();
    });
  }

  private reconcileDependentSelectValues(rowData: any): void {
    const selectFields = this.gFormUIC.filter(f => {
      const inputType = (f.uiInputType || '').toLowerCase();
      return inputType === 'select' || inputType === 'selectnadd';
    });

    selectFields.forEach(field => {
      const control = this.gFormGroup.get(field.dbFeildName!);
      if (!control) return;

      const currentValue = control.value;
      if (currentValue !== null && currentValue !== undefined && `${currentValue}`.trim() !== '') {
        return;
      }

      if (!Array.isArray(field.data) || field.data.length === 0) {
        return;
      }

      const directRowValue = this.getObjectValueCaseInsensitive(rowData, field.dbFeildName!);
      if (directRowValue !== undefined && directRowValue !== null && `${directRowValue}`.trim() !== '') {
        control.patchValue(directRowValue, { emitEvent: false });
        return;
      }

      const dependents = this.gFormUIC.filter(
        child => (child.dependentOn || '').toLowerCase() === (field.dbFeildName || '').toLowerCase()
      );

      for (const child of dependents) {
        const childSavedValue = this.getObjectValueCaseInsensitive(rowData, child.dbFeildName!);
        if (childSavedValue === undefined || childSavedValue === null || `${childSavedValue}`.trim() === '') {
          continue;
        }

        const sourceKey = (child.dependentOnKey || child.patchPropertyName || field.patchPropertyName || 'key').trim();
        const bindKey = (field.patchPropertyName || 'key').trim();

        const matchedOption = field.data.find((item: any) => {
          const optionValue = this.getObjectValueCaseInsensitive(item, sourceKey);
          return optionValue !== undefined && optionValue !== null && `${optionValue}` === `${childSavedValue}`;
        });

        if (!matchedOption) {
          continue;
        }

        const valueToSelect = this.getObjectValueCaseInsensitive(matchedOption, bindKey)
          ?? this.getObjectValueCaseInsensitive(matchedOption, sourceKey);

        if (valueToSelect !== undefined && valueToSelect !== null && `${valueToSelect}`.trim() !== '') {
          control.patchValue(valueToSelect, { emitEvent: false });
          break;
        }
      }
    });
  }

  // Runs jsexecutor fields whose script is a "pure" option-provider — i.e. it only
  // rewrites a sibling dbquery field's dataFetchFrom and calls dynamicQuery /
  // dynamicQueryForInstance, rather than writing a value onto any control itself
  // (that's what distinguishes itemIdPatch/indentList from value-setting scripts
  // like departmentAssign/assignProj, which must stay blocked during patch so saved
  // data isn't clobbered). Safe to run mid-patch because isPatchingInProgress is
  // still true, so the preserved-value branch inside dynamicQuery/dynamicQueryForInstance
  // matches the already-patched saved value against the newly loaded options instead
  // of overwriting it. Restricted to hidden (isUIVisible === 0) fields — the existing
  // convention for these wiring-only helper fields — to avoid accidentally re-enabling
  // some other visible jsexecutor elsewhere on the platform during patch.
  private async runOptionProviderJsExecutors(): Promise<void> {
    const providers = this.gFormUIC.filter(p =>
      p.dataFetchType?.toLowerCase() === 'jsexecutor' &&
      !!p.dataFetchFrom &&
      p.isUIVisible === 0 &&
      !p.dataFetchFrom.includes('.patchValue(') &&
      !p.dataFetchFrom.includes('.setValue(')
    );

    if (providers.length === 0) return;

    const formInstance = this.enableMultipleForms
      ? this.formInstances?.find(i => i.formGroup === this.gFormGroup)
      : undefined;

    providers.forEach(provider => this.executeJavaScript(provider, formInstance));

    await Promise.all(
      this.gFormUIC
        .filter(f => f.isLoading)
        .map(f => this.waitForFieldToLoad(f))
    );
  }

  async patchWithDependencies(
    data: any | any[],
    options: { forceMultiple?: boolean } = {}
  ): Promise<void> {
    const rows = Array.isArray(data)
      ? data
      : (data != null ? [data] : []);

    const shouldUseMultiple = options.forceMultiple ?? this.enableMultipleForms;

    if (!shouldUseMultiple) {
      if (rows.length > 0) {
        await this.patchSingleFormWithDependencies(rows[0]);
      }
      return;
    }

    const originalFormGroup = this.gFormGroup;
    const originalFormUIC = this.gFormUIC;

    this.formInstances = [];

    if (rows.length === 0) {
      this.addForm();
      return;
    }

    this.formInstances.push({
      formGroup: this.gFormGroup,
      formUIC: this.gFormUIC,
      index: 0
    });

    for (let index = 1; index < rows.length; index++) {
      this.addForm();
    }

    for (let index = 0; index < rows.length; index++) {
      const instance = this.formInstances[index];
      if (!instance?.formGroup) {
        continue;
      }

      this.gFormGroup = instance.formGroup;
      this.gFormUIC = instance.formUIC;
      try {
        await this.patchSingleFormWithDependencies(rows[index]);
      } catch (err) {
        // Isolate rows from each other: an uncaught error patching one row (e.g. a
        // malformed/unexpected value in that row's data) must not abort this for-of
        // loop and leave every LATER row completely unpatched/unfetched - each row
        // is otherwise independent. Without this try/catch a single bad row silently
        // stops all subsequent rows, which looks exactly like "only the first row(s)
        // patch, the rest are blank" with far fewer network calls than expected.
        console.error(`❌ [patchWithDependencies] Row ${index} failed to patch, continuing with remaining rows:`, err);
      }
    }

    this.gFormGroup = this.formInstances[0]?.formGroup ?? originalFormGroup;
    this.gFormUIC = this.formInstances[0]?.formUIC ?? originalFormUIC;
  }

  private executeAllJSExecutorsForCurrentForm(): void {
    const jsExecutorFields = this.gFormUIC.filter(
      f => f.dataFetchType?.toLowerCase() === 'jsexecutor' && !!f.dataFetchFrom
    );

    jsExecutorFields.forEach(field => {
      this.executeJavaScript(field);
    });
  }

  private loadApiForField(f: GenericFormUICDTO, filterValue?: any): Promise<void> {
    return new Promise((resolve, reject) => {
      // ✅ Skip if already loading or loaded
      if (f.isLoading || f.dataLoaded) {
        console.log(`⏭️ Skipping loadApiForField for ${f.dbFeildName} - already loading or loaded`);
        resolve();
        return;
      }

      f.isLoading = true;

      const dependentValue = f.dependentOn
        ? this.gFormGroup.get(f.dependentOn)?.value
        : null;

      const effectiveFilterValue = filterValue ?? dependentValue;
      const url = this.formatAPIURL(f, effectiveFilterValue);

      // Preserve the option object for the value already sitting in the control (typically
      // seeded by the parent component from grid row data before patching) so an "available/
      // pending only" endpoint - e.g. GetAvailableIndent, which excludes the indent already
      // linked to the PO being edited - doesn't wipe out the one entry ng-select needs to
      // render that existing selection's label.
      const selectedValue = this.gFormGroup.get(f.dbFeildName!)?.value;
      const bindKey = (f.patchPropertyName || 'key').trim();
      const hasSelectedValue = selectedValue !== null && selectedValue !== undefined && `${selectedValue}`.trim() !== '';
      const preservedItem = hasSelectedValue
        ? (Array.isArray(f.data) ? f.data : []).find((item: any) => {
            const v = this.getObjectValueCaseInsensitive(item, bindKey);
            return v !== undefined && v !== null && `${v}` === `${selectedValue}`;
          })
        : null;

      this.http.get(url)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            f.data = Array.isArray(res?.items) ? res.items : res;

            if (preservedItem && Array.isArray(f.data)) {
              const stillPresent = f.data.some((item: any) => {
                const v = this.getObjectValueCaseInsensitive(item, bindKey);
                return v !== undefined && v !== null && `${v}` === `${selectedValue}`;
              });
              if (!stillPresent) {
                f.data = [preservedItem, ...f.data];
              }
            }

            f.isLoading = false;
            f.dataLoaded = true;
            if (this.isPatchingInProgress) {
              this.applyDeferredPatchDependencies(f);
            }
            resolve();
          },
          error: () => {
            f.isLoading = false;
            f.dataLoaded = false; // don’t block chain
          }
        });
    });
  }

  private loadDbQueryForField(f: GenericFormUICDTO, filterValue?: any): Promise<void> {
    return new Promise((resolve) => {
      // ✅ Skip if already loading or loaded
      if (f.isLoading || f.dataLoaded) {
        console.log(`⏭️ Skipping loadDbQueryForField for ${f.dbFeildName} - already loading or loaded`);
        resolve();
        return;
      }

      // ✅ No SQL to run (e.g. the list is built/pushed at runtime by a jsexecutor).
      // Running an empty query would return [] and wipe any option seeded for edit/patch.
      if (!f.dataFetchFrom || !f.dataFetchFrom.trim()) {
        console.log(`⏭️ Skipping loadDbQueryForField for ${f.dbFeildName} - no SQL (jsexecutor-fed); preserving seeded data`);
        if (Array.isArray(f.data) && f.data.length > 0) {
          f.dataLoaded = true;
        }
        resolve();
        return;
      }

      const searchTerm = filterValue ?? '';
      const body = {
        dynamicSQL: this.formatAPIURL(f, searchTerm)
      };

      f.isLoading = true;

      const selectedValue = this.gFormGroup.get(f.dbFeildName!)?.value;
      const bindKey = (f.patchPropertyName || 'key').trim();
      const hasSelectedValue = selectedValue !== null && selectedValue !== undefined && `${selectedValue}`.trim() !== '';
      const preservedItem = hasSelectedValue
        ? (Array.isArray(f.data) ? f.data : []).find((item: any) => {
            const v = this.getObjectValueCaseInsensitive(item, bindKey);
            return v !== undefined && v !== null && `${v}` === `${selectedValue}`;
          })
        : null;

      this.http.post(PrintMaster_DynamicSQLExecutor, body)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {

            f.data = Array.isArray(res)
              ? res
              : Array.isArray(res?.items)
                ? res.items
                : Array.isArray(res?.data)
                  ? res.data
                  : [];

            if (preservedItem && Array.isArray(f.data)) {
              const stillPresent = f.data.some((item: any) => {
                const v = this.getObjectValueCaseInsensitive(item, bindKey);
                return v !== undefined && v !== null && `${v}` === `${selectedValue}`;
              });
              if (!stillPresent) {
                f.data = [preservedItem, ...f.data];
              }
            }

            f.isLoading = false;
            f.dataLoaded = true;
            if (this.isPatchingInProgress) {
              this.applyDeferredPatchDependencies(f);
            }
            resolve();
          },
          error: () => {
            f.isLoading = false;
            f.dataLoaded = false;
            resolve();
          }
        });
    });
  }

  private applyDeferredPatchDependencies(parentField: GenericFormUICDTO): void {
    if (!parentField?.dbFeildName) {
      return;
    }

    const parentKey = parentField.dbFeildName.toLowerCase();
    const childPatchFields = this.gFormUIC.filter(child =>
      (child.dataFetchType || '').toLowerCase() === 'patch' &&
      (child.dependentOn || '').toLowerCase() === parentKey
    );

    childPatchFields.forEach(child => {
      const fieldKey = child.dbFeildName!.toLowerCase();

      // ✅ NEW: Skip if rowData already had value
      if (this.patchedValues.has(fieldKey)) {
        console.log(`🛑 Skipping deferred patch for ${child.dbFeildName}`);
        return;
      }
      const childControl = this.gFormGroup.get(child.dbFeildName!);
      const currentValue = childControl?.value;
      const preservedValue = this.patchedValues.get(child.dbFeildName!.toLowerCase());

      // ✅ If we have a preserved value from rowData, keep it - don't overwrite with parent-derived value
      if (preservedValue !== undefined) {
        console.log(`🔒 Preserving rowData value for PATCH field ${child.dbFeildName}: ${preservedValue}`);
        // Don't patch from parent - the rowData value is intentionally preserved
        return;
      }

      // ✅ Only patch from parent if there's no current value AND no preserved rowData
      if (currentValue === null || currentValue === undefined || `${currentValue}`.trim() === '') {
        console.log(`📝 Patching PATCH field ${child.dbFeildName} from parent (no rowData)`);
        this.patchFromParent(child);
      }
    });
  }

  private patchFromParent(f: GenericFormUICDTO) {
    if (!f.dependentOn) return;

    const fieldKey = f.dbFeildName!.toLowerCase();

    // ✅ NEW: If value came from rowData → DO NOT override
    if (this.patchedValues.has(fieldKey)) {
      const existingValue = this.gFormGroup.get(f.dbFeildName!)?.value;

      if (existingValue !== null && existingValue !== undefined && `${existingValue}`.trim() !== '') {
        console.log(`🛑 Skipping patchFromParent for ${f.dbFeildName} (value already from rowData)`);
        return;
      }
    }
    const parent = this.gFormUIC.find(
      x => x.dbFeildName!.toLowerCase() === f.dependentOn!.toLowerCase()
    );

    if (!parent || !parent.data?.length) return;

    const parentValue = this.gFormGroup.get(parent.dbFeildName!)?.value;
    const parentBindKey = (parent.patchPropertyName || 'key').trim();
    const match = parent.data.find((item: any) => {
      const matchValue = this.getObjectValueCaseInsensitive(item, parentBindKey);
      return matchValue !== undefined && matchValue !== null && `${matchValue}` === `${parentValue}`;
    });

    if (match) {
      const sourceKey = (f.patchPropertyName || f.dependentOnKey || '').trim();
      if (!sourceKey) {
        return;
      }

      const patchedValue = this.getObjectValueCaseInsensitive(match, sourceKey);
      if (patchedValue === undefined) {
        return;
      }

      this.gFormGroup
        .get(f.dbFeildName!)
        ?.patchValue(patchedValue, { emitEvent: false });
    }
  }

  // ============== JS EXECUTOR METHODS ==============

  private setupJSExecutorListener(field: GenericFormUICDTO, formInstance?: any): void {
    const activeFormGroup: FormGroup = formInstance?.formGroup ?? this.gFormGroup;

    if (!activeFormGroup || !field.dataFetchFrom) return;

    try {
      // Extract all field references from the JavaScript code
      const dependentFields = this.extractFieldReferences(field.dataFetchFrom);

      // Subscribe to changes in all dependent fields
      dependentFields.forEach(fieldName => {
        const control = activeFormGroup.get(fieldName);

        if (control) {
          control.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
          ).subscribe((value) => {
            console.log(`🔔 ${fieldName} changed to:`, value);
            if (this.isPatchingInProgress) {
              console.log(`⛔ JSExecutor blocked during patch`);
              return;
            }
            this.executeJavaScript(field, formInstance);
          });
        }
      });

      // ✅ Wire the script exactly once (no duplicate valueChanges subscriptions)
      const wireKey = `${field.dbFeildName}#${formInstance?.index ?? 'single'}`;
      if (this.jsExecutorWired.has(wireKey)) return;
      this.jsExecutorWired.add(wireKey);

      // Source dropdowns the script reads (e.g. itemId) — non-dependent dbquery/api fields.
      const activeFormUIC: GenericFormUICDTO[] = formInstance?.formUIC ?? this.gFormUIC;
      const sourceFields = activeFormUIC.filter((s: GenericFormUICDTO) =>
        ['dbquery', 'api'].includes((s.dataFetchType || '').toLowerCase()) &&
        (!s.dependentOn || !s.dependentOn.trim()));

      // Run only after the source data has loaded so the script's item lookup succeeds.
      // Never run during/after edit — the saved values are already set and must not be
      // recalculated or overwritten.
      Promise.all(sourceFields.map(s => this.waitForFieldToLoad(s))).then(() => {
        if (this.isEditMode || this.isPatchingInProgress) {
          console.log(`⏭️ Skipping JSExecutor for ${field.dbFeildName} - edit mode`);
          return;
        }
        console.log(`⏰ Running JSExecutor for ${field.dbFeildName} after source data loaded`);
        this.executeJavaScript(field, formInstance);
      });
    } catch (error) {
      console.error(`Error setting up JSExecutor for ${field.dbFeildName}:`, error);
    }
  }

  private extractFieldReferences(jsCode: string): string[] {
    // Extract field references in format: form.fieldName, form['fieldName'], formData.fieldName, formData['fieldName']
    const fieldNames = new Set<string>();

    // Match: form.fieldName
    const dotNotationRegex = /form\.(\w+)/g;
    let match;
    while ((match = dotNotationRegex.exec(jsCode)) !== null) {
      fieldNames.add(match[1]);
    }

    // Match: form['fieldName'] or form["fieldName"]
    const bracketNotationRegex = /form\[['"](\w+)['"]\]/g;
    while ((match = bracketNotationRegex.exec(jsCode)) !== null) {
      fieldNames.add(match[1]);
    }

    // Match: formData.fieldName
    const formDataDotNotationRegex = /formData\.(\w+)/g;
    while ((match = formDataDotNotationRegex.exec(jsCode)) !== null) {
      fieldNames.add(match[1]);
    }

    // Match: formData['fieldName'] or formData["fieldName"]
    const formDataBracketNotationRegex = /formData\[['"](\w+)['"]\]/g;
    while ((match = formDataBracketNotationRegex.exec(jsCode)) !== null) {
      fieldNames.add(match[1]);
    }

    return Array.from(fieldNames);
  }

  private executeJavaScript(field: GenericFormUICDTO, formInstance?: any): void {
    const activeFormGroup: FormGroup = formInstance?.formGroup ?? this.gFormGroup;
    const activeFormUIC: GenericFormUICDTO[] = formInstance?.formUIC ?? this.gFormUIC;

    if (!activeFormGroup || !field.dataFetchFrom || !field.dbFeildName) return;

    // Hard stop for value-setting scripts (e.g. departmentAssign) once edit mode is
    // active: this.isEditMode never resets back to false for the lifetime of the
    // component instance, so this single check closes every remaining call path
    // (initial source-data-loaded run, valueChanges-triggered re-run, etc.) instead
    // of relying on each caller to individually guard isEditMode/isPatchingInProgress.
    // Saved data must always win over a script keyed off the current logged-in user.
    const isValueSettingScript = field.dataFetchFrom.includes('.patchValue(') || field.dataFetchFrom.includes('.setValue(');
    if (this.isEditMode && isValueSettingScript) {
      console.log(`⛔ [JSExecutor] Blocked value-setting script for "${field.dbFeildName}" — edit mode is active, saved values must not be overwritten.`);
      return;
    }

    try {
      console.log('[JSExecutor] Executing script for field:', field.dbFeildName, {
        hasFormInstance: !!formInstance,
        formIndex: formInstance?.index ?? null
      });
      // Create a form proxy object with all current form values
      const formValues: any = {};

      activeFormUIC.forEach(f => {
        if (f.dbFeildName) {
          const value = activeFormGroup.get(f.dbFeildName)?.value;
          formValues[f.dbFeildName] = value !== null && value !== undefined ? value : null;
        }
      });

      // ✅ ENHANCED: Create formData object with full selected item data for dropdowns
      const formData: Record<string, any> = {};

      activeFormUIC.forEach(f => {
        if (f.dbFeildName && (f.uiInputType === 'select' || f.uiInputType === 'selectnadd')) {
          const value = activeFormGroup.get(f.dbFeildName)?.value;

          if (value && f.data && Array.isArray(f.data)) {
            const idProperty = f.patchPropertyName || 'id';
            const selectedItem = f.data.find(item => {
              return item[idProperty] === value || item[idProperty]?.toString() === value?.toString();
            });

            if (selectedItem) {
              formData[f.dbFeildName] = selectedItem;
              console.log(`[JSExecutor] Found dropdown data for ${f.dbFeildName}:`, selectedItem);
            } else {
              console.warn(`[JSExecutor] No dropdown data found for ${f.dbFeildName}, value:`, value);
            }
          }
        }
      });

      // ✅ NEW: If we don't have formData for dependent field, try to get it from patched text fields
      if (field.dependentOn && !formData[field.dependentOn]) {
        // Check if there's a text field that has the patched data
        const dependentField = activeFormUIC.find(f => f.dbFeildName === field.dependentOn);
        if (dependentField && field.dependentOnKey) {
          // Look for a field that has the dependentOnKey as its dbFeildName
          const patchedTextField = activeFormUIC.find(f => f.dbFeildName === field.dependentOnKey);
          if (patchedTextField) {
            const patchedValue = activeFormGroup.get(field.dependentOnKey)?.value;
            console.log(`[JSExecutor] Using patched text field ${field.dependentOnKey}:`, patchedValue);

            // Create a mock object with the patched value
            formData[field.dependentOn] = {
              [field.dependentOnKey]: patchedValue
            };
          }
        }
      }

      const gFormUIC = activeFormUIC;
      const component = this;

      // Create execution context with safe built-in functions
      const context = {
        form: formValues,
        formData: formData,
        gFormUIC: gFormUIC,
        component: component,
        formInstance: formInstance ?? null,
        Math: Math,
        Date: Date,
        parseFloat: parseFloat,
        parseInt: parseInt,
        Number: Number,
        String: String,
        Boolean: Boolean,
        console: {
          log: (...args: any[]) => console.log('[JSExecutor]', ...args),
          warn: (...args: any[]) => console.warn('[JSExecutor]', ...args),
          error: (...args: any[]) => console.error('[JSExecutor]', ...args)
        }
      };

      // Execute the JavaScript code
      const result = this.safeExecute(field.dataFetchFrom, context);

      // Update the field value without triggering change events
      const control = activeFormGroup.get(field.dbFeildName);
      if (control) {
        control.setValue(result, { emitEvent: false });
      }
    } catch (error) {
      console.error(`Error executing JavaScript for ${field.dbFeildName}:`, error);
    }
  }

  private safeExecute(code: string, context: any): any {
    try {
      // Create parameter names from context keys
      const paramNames = Object.keys(context);
      const paramValues = Object.values(context);

      // Wrap the code in a function and execute it in strict mode
      const func = new Function(...paramNames, `
        'use strict';
        try {
          return (function() {
            ${code}
          })();
        } catch (error) {
          console.error('JSExecutor Runtime Error:', error);
          return null;
        }
      `);

      return func(...paramValues);
    } catch (error) {
      console.error('JSExecutor Compilation Error:', error);
      return null;
    }
  }

  // ✅ Add these two helper methods to your component class

  /**
   * Setup dbquery triggers for a form instance
   */
  private setupDbQueryTriggersForInstance(
    clonedUIC: GenericFormUICDTO[],
    formGroup: FormGroup,
    formInstance: any
  ): void {
    clonedUIC.forEach(field => {

      if (field.dataFetchType?.toLowerCase() === 'dbquery' && field.dataFetchFrom) {
        // Extract placeholders from SQL query (e.g., {{ItemId}}, {{ReqQty}})
        const placeholders = this.extractPlaceholdersFromSQL(field.dataFetchFrom);

        console.log(`🔍 DBQuery field "${field.dbFeildName}" depends on:`, placeholders);

        // Setup listeners for all dependent fields
        placeholders.forEach(placeholder => {
          const control = formGroup.get(placeholder);

          if (control) {
            control.valueChanges.pipe(
              debounceTime(500),
              distinctUntilChanged(),
              takeUntil(this.destroy$)
            ).subscribe((value) => {
              // Only trigger if all required values are present
              const allPlaceholdersFilled = placeholders.every(p => {
                const val = formGroup.get(p)?.value;
                return val !== null && val !== undefined && val !== '' &&
                  (typeof val !== 'number' || val > 0);
              });

              if (allPlaceholdersFilled) {
                console.log(`✅ Triggering dbquery for ${field.dbFeildName}`);
                // ✅ Pass the formInstance to get correct form values
                this.dynamicQueryForInstance('search', field, formInstance, true);
              } else {
                console.log(`⏭️ Skipping dbquery - not all placeholders filled yet`);
                console.log(`   Missing values for: ${placeholders.filter(p => {
                  const val = formGroup.get(p)?.value;
                  return !val || (typeof val === 'number' && val <= 0);
                }).join(', ')}`);
              }
            });
          } else {
            console.warn(`⚠️ Control not found for placeholder: ${placeholder}`);
          }
        });
      }
    });
  }

  /**
   * Extract SQL placeholders like {{ItemId}}, {{ReqQty}}
   */
  private extractPlaceholdersFromSQL(sql: string): string[] {
    const placeholders = new Set<string>();

    // Match {{FieldName}} pattern
    const regex = /\{\{(\w+)\}\}/g;
    let match;

    while ((match = regex.exec(sql)) !== null) {
      const fieldName = match[1];
      // Ignore system placeholders
      if (fieldName.toLowerCase() !== 'loggedincompanyid' &&
        fieldName.toLowerCase() !== 'branchid' &&
        fieldName.toLowerCase() !== 'userid' &&
        fieldName.toLowerCase() !== 'companyid') {
        // Convert to camelCase to match form control names
        const camelCase = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
        placeholders.add(camelCase);
      }
    }

    return Array.from(placeholders);
  }

  /**
   * Execute dynamic query for a specific form instance
   */
  dynamicQueryForInstance(
    searchKey: any,
    inputConfig: GenericFormUICDTO,
    formInstance: any,
    patchValue: boolean = false
  ): void {
    if (!inputConfig.dataFetchFrom || inputConfig.dataFetchFrom.trim() === '') {
      console.warn('⚠️ No SQL query defined for field:', inputConfig.dbFeildName);
      return;
    }

    const { formGroup, index } = formInstance;

    console.log('[DBQuery][Instance] Start', {
      field: inputConfig.dbFeildName,
      searchKey,
      patchValue,
      formIndex: index,
      isPatchingInProgress: this.isPatchingInProgress
    });
    // ✅ Check if we have a preserved value for this field
    const fieldKey = inputConfig.dbFeildName.toLowerCase();
    const preservedValue = this.patchedValues.get(fieldKey);

    console.log(`📊 Executing dynamic query for ${inputConfig.dbFeildName} in form instance ${index}`);

    const searchTerm =
      searchKey && typeof searchKey === 'object' && !Array.isArray(searchKey)
        ? (searchKey.term ?? '')
        : (searchKey ?? '');

    // ✅ Use formatAPIURLForInstance to replace placeholders
    const formattedSQL = this.formatAPIURLForInstance(inputConfig, searchTerm, formGroup);
    console.log('[DBQuery][Instance] Formatted SQL:', formattedSQL);

    const payload = {
      dynamicSQL: formattedSQL
    };

    inputConfig.isLoading = true;
    const headers = {
      CompanyId: '1',
      BranchId: '1',
      SoftwareId: '1'
    };


    this.http.post(PrintMaster_DynamicSQLExecutor, payload, { headers })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          console.log(`✅ DBQuery result for ${inputConfig.dbFeildName}:`, res);

          const resultItems = Array.isArray(res)
            ? res
            : Array.isArray(res?.items)
              ? res.items
              : Array.isArray(res?.data)
                ? res.data
                : [];

          if (resultItems.length > 0) {
            console.log('[DBQuery][Instance] Result count:', resultItems.length);

            // ✅ IMPROVED: Handle preserved value during patching
            if (this.isPatchingInProgress && preservedValue !== undefined) {
              console.log(`🔒 [Instance] Preserving patched value for ${inputConfig.dbFeildName}:`, preservedValue);

              // ✅ CRITICAL: Always use patchPropertyName (the DB field)
              if (!inputConfig.patchPropertyName || inputConfig.patchPropertyName.trim() === '') {
                console.error(`❌ CRITICAL [Instance]: Missing patchPropertyName for ${inputConfig.dbFeildName}. Cannot match preserved value!`);
                formGroup.get(inputConfig.dbFeildName)?.patchValue(preservedValue, { emitEvent: false });
              } else {
                const patchKey = inputConfig.patchPropertyName.trim();
                console.log(`  ✅ [Instance] Using patchPropertyName (DB field): ${patchKey}`);

                const matchedItem = resultItems.find((item: any) => {
                  const itemValue = this.getObjectValueCaseInsensitive(item, patchKey);
                  return itemValue !== undefined && itemValue !== null && `${itemValue}` === `${preservedValue}`;
                });

                if (matchedItem) {
                  console.log(`✅ [Instance] Found matching item for preserved value:`, matchedItem);
                  formGroup.get(inputConfig.dbFeildName)?.patchValue(preservedValue, { emitEvent: false });
                  this.onSelectChangeForInstance(matchedItem, inputConfig, formInstance);
                } else {
                  console.warn(`⚠️ [Instance] Preserved value ${preservedValue} not found in results`);
                  formGroup.get(inputConfig.dbFeildName)?.patchValue(preservedValue, { emitEvent: false });
                }
              }
            } else if (patchValue && !this.isPatchingInProgress) {
              // ✅ Normal mode - patch first value
              // ✅ CRITICAL: Always use patchPropertyName (the DB field)
              if (!inputConfig.patchPropertyName || inputConfig.patchPropertyName.trim() === '') {
                console.error(`❌ CRITICAL [Instance]: Missing patchPropertyName for ${inputConfig.dbFeildName}. Cannot auto-patch value!`);
              } else {
                const patchKey = inputConfig.patchPropertyName.trim();
                const resultData = resultItems[0];
                const valueToPath = this.getObjectValueCaseInsensitive(resultData, patchKey);
                console.log('[DBQuery][Instance] Auto-patch first value:', valueToPath);

                if (valueToPath !== undefined && valueToPath !== null) {
                  console.log(`📝 [Instance] Auto-patching ${inputConfig.dbFeildName} using ${patchKey}:`, valueToPath);
                  formGroup.get(inputConfig.dbFeildName)?.patchValue(valueToPath, { emitEvent: false });
                  this.onSelectChangeForInstance(resultData, inputConfig, formInstance);
                }
              }
            }

            // Store data for later use
            inputConfig.data = resultItems;
          } else {
            console.warn(`⚠️ No data returned for ${inputConfig.dbFeildName}`);
            inputConfig.data = [];


            // Only reset if not preserving a value
            if (patchValue && !this.isPatchingInProgress) {
              formGroup.get(inputConfig.dbFeildName)?.patchValue(null);
            }
          }

          inputConfig.isLoading = false;
          inputConfig.dataLoaded = true;
        },
        error: (error) => {
          console.error(`❌ Error executing dbquery for ${inputConfig.dbFeildName}:`, error);
          inputConfig.isLoading = false;
          inputConfig.dataLoaded = false;
        }
      });
  }

  addForm() {
    const formIndex = this.formInstances.length;

    // Deep clone the UIC config for this form instance
    const clonedUIC = JSON.parse(JSON.stringify(this.gFormUIC)) as GenericFormUICDTO[];

    // Create form group for this instance
    const group: { [key: string]: FormControl } = {};
    const apiCalls: { [key: string]: any } = {};

    clonedUIC.forEach(f => {
      // Setup dependent fields
      let dependentFields = clonedUIC.filter(i =>
        i.dependentOn &&
        i.dependentOn!.trim().length > 0 &&
        i.dependentOn.toLowerCase() == f.dbFeildName.toLowerCase()
      );
      f.dependentFields = dependentFields;

      const validators: any[] = [];

      if (f.validationReq === 1 && f.isUIVisible == 1) {
        validators.push(Validators.required);
      }

      if (f.validationPattern && f.validationPattern.startsWith('^') && f.isUIVisible == 1) {
        validators.push(Validators.pattern(new RegExp(f.validationPattern)));
      }

      if (f.validationPattern === 'noFutureDate' && f.uiInputType?.toLowerCase() === 'date' && f.isUIVisible == 1) {
        validators.push((control: FormControl) => {
          if (!control.value) return null;
          return control.value > new Date().toISOString().split('T')[0] ? { noFutureDate: true } : null;
        });
      }

      if (f.validationPattern === 'noPastDate' && f.uiInputType?.toLowerCase() === 'date' && f.isUIVisible == 1) {
        validators.push((control: FormControl) => {
          if (!control.value) return null;
          return control.value < new Date().toISOString().split('T')[0] ? { noPastDate: true } : null;
        });
      }

      let dVal: any = f.defaultValue;
      if (f.uiInputType?.toLowerCase() !== "select" && f.uiInputType?.toLowerCase() !== "selectnadd") {
        if (f.formDataType?.toLowerCase() === 'number') {
          dVal = dVal != null && dVal != undefined && dVal.trim() != '' ? Number(dVal) : 0;
        } else if (f.formDataType?.toLowerCase() === 'string') {
          dVal = dVal ?? '';
        } else if (f.formDataType?.toLowerCase() === 'date') {
          dVal = dVal != null && dVal != undefined && dVal.trim() != '' ? dVal : f.otherDetails?.toLowerCase() === 'patchdate' ? formatDateForInput(new Date()) : null;
        }
        group[f.dbFeildName!] = new FormControl(dVal, validators);
      } else {
        group[f.dbFeildName!] = new FormControl(dVal.trim() == '' ? null : dVal, validators);
      }
    });

    // ✅ CREATE the FormGroup FIRST
    const newFormGroup = new FormGroup(group);

    // Built now (rather than after the loops below) because the dbquery auto-load
    // logic further down needs a formInstance to pass into dynamicQueryForInstance.
    const formInstance = {
      formGroup: newFormGroup,
      formUIC: clonedUIC,
      index: formIndex
    };

    // ✅ NOW make API calls with the correct formGroup (excluding dbquery)
    clonedUIC.forEach(f => {
      // ✅ Only load regular API calls, NOT dbquery, and NOT dependent fields (e.g.
      // vendorId depending on itemId) - eagerly loading those here runs before the
      // parent control has a real value, so {{DependentOnId}} resolves to 0. That
      // response can race with and overwrite the correctly parented fetch triggered
      // later during patch, leaving the saved value unselectable in the UI (see
      // matching skip in the single-form init above).
      if (f.searchable != 1 &&
        f.dataFetchType &&
        f.dataFetchType!.toLowerCase() == 'api' &&
        f.isUIVisible == 1 &&
        (!f.dependentOn || f.dependentOn.trim() === '')) {
        f.isLoading = true;
        apiCalls[f.dbFeildName] = this.http.get(environment.getApiUrl(this.formatAPIURLForInstance(f, '', newFormGroup)));
      }
    });

    // Batch all API calls with forkJoin for this form instance
    if (Object.keys(apiCalls).length > 0) {
      forkJoin(apiCalls).pipe(takeUntil(this.destroy$)).subscribe({
        next: (results: any) => {
          Object.keys(results).forEach((dbFieldName) => {
            const f = clonedUIC.find(field => field.dbFeildName === dbFieldName);
            if (f) {
              const res: any = results[dbFieldName];
              if (Array.isArray(res?.items) && res['items'].length > 0) {
                f.data = res.items.map((item: any) => {
                  const patchedKey = f.patchPropertyName
                    ? Object.keys(item).find(key =>
                      key.trim().toLowerCase() === f.patchPropertyName!.trim().toLowerCase()
                    )
                    : undefined;

                  if (patchedKey && f.formDataType?.trim().toLowerCase() === 'string') {
                    return {
                      ...item,
                      [patchedKey]: String(item[patchedKey])
                    };
                  }
                  return item;
                });

                if (f.defaultValue && f.defaultValue?.trim() != '' && newFormGroup.get(f.dbFeildName)?.value && newFormGroup.get(f.dbFeildName)?.value.trim() == '') {
                  newFormGroup.get(f.dbFeildName)?.patchValue(f.defaultValue);
                }
              } else {
                f.data = [res];
                if (
                  f.uiInputType?.toLowerCase() == 'text' &&
                  newFormGroup.get(f.dbFeildName) &&
                  (typeof newFormGroup.get(f.dbFeildName)?.value === 'string' ? newFormGroup.get(f.dbFeildName)?.value.trim() === '' : !newFormGroup.get(f.dbFeildName)?.value)
                ) {
                  newFormGroup.get(f.dbFeildName)?.patchValue(res[f.patchPropertyName.trim()]);
                }
              }
              f.isLoading = false;
            }
          });
        },
        error: (err) => {
          console.error('Error fetching API data for form', formIndex, err);
          Object.keys(apiCalls).forEach((dbFieldName) => {
            const f = clonedUIC.find(field => field.dbFeildName === dbFieldName);
            if (f) f.isLoading = false;
          });
        }
      });
    }

    // Initialize dataLoaded flag and handle searchable fields
    clonedUIC.forEach(f => {
      if (!f.hasOwnProperty('dataLoaded')) {
        f.dataLoaded = false;
      }

      // ❌ DON'T auto-load dependent dbquery fields - they are triggered by setupDbQueryTriggersForInstance
      // once their parent field(s) have a value. Fields with no dependentOn (e.g. itemId, whose SQL only
      // references system placeholders like {{LoggedInCompanyId}}) have nothing to trigger them, so load
      // those immediately instead of leaving them empty until the user types into the search box.
      if (f.dataFetchType && f.dataFetchType.toLowerCase() == 'dbquery') {
        if (f.dependentOn && f.dependentOn.trim() !== '') {
          console.log(`⏭️ Skipping auto-load for dependent dbquery field: ${f.dbFeildName}`);
          return; // Skip - will be triggered by setupDbQueryTriggersForInstance
        }
        this.dynamicQueryForInstance(null, f, formInstance, false);
        return;
      }

      // Handle searchable fields with API
      if (f.searchable && f.searchable == 1 && f.dataFetchType && f.dataFetchType.toLowerCase() == 'api') {
        f.isLoading = true;

        this.http.get(environment.getApiUrl(this.formatAPIURLForInstance(f, '', newFormGroup)))
          .pipe(takeUntil(this.destroy$))
          .subscribe((res: any) => {
            f.data = res.items || [];
            f.isLoading = false;
          });

        // Setup valueChanges for searchable fields
        newFormGroup.get(f.dbFeildName)?.valueChanges
          .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
          .subscribe((searchTerm) => {
            if (f.dependentFields.length > 0) {
              if (!f.uiInputType?.trim().includes('select')) {
                f.dependentFields.forEach(uicFields => {
                  if (uicFields.dataFetchType!.toLowerCase() == 'api') {
                    this.http.get(environment.getApiUrl(this.formatAPIURLForInstance(uicFields, searchTerm, newFormGroup)))
                      .pipe(takeUntil(this.destroy$))
                      .subscribe((res: any) => {
                        uicFields.data = res.items || [];
                      });
                  } else if (uicFields.dataFetchType!.toLowerCase() == 'patch') {
                    let data = f.data;

                    if (uicFields.patchPropertyName.trim() != '') {
                      const formValue = newFormGroup.get(f.dbFeildName)!.value?.toString().toLowerCase();

                      const matchedObject = data.find(i => {
                        const matchingKey = Object.keys(i).find(
                          key => key.toLowerCase() === f.patchPropertyName.toLowerCase()
                        );
                        return matchingKey && i[matchingKey]?.toString().toLowerCase() === formValue;
                      });

                      if (matchedObject) {
                        let value;
                        const matchedKey = Object.keys(matchedObject).find(key =>
                          key.toLowerCase() === uicFields.patchPropertyName.toLowerCase()
                        );

                        if (matchedKey) {
                          value = matchedObject[matchedKey];
                        }
                        newFormGroup.get(uicFields.dbFeildName)?.patchValue(value);
                      } else {
                        newFormGroup.get(uicFields.dbFeildName)?.patchValue(null);
                      }
                    } else {
                      let value = data.find(i => i.key == newFormGroup.get(f.dbFeildName)!.value).value;
                      newFormGroup.get(uicFields.dbFeildName)?.patchValue(value);
                    }
                  } else if (uicFields.dataFetchType!.toLowerCase() == 'apinpatch' && this.formatAPIURLForInstance(uicFields, searchTerm, newFormGroup)) {
                    uicFields.isLoading = true;
                    this.http.get(environment.getApiUrl(this.formatAPIURLForInstance(uicFields, searchTerm, newFormGroup)))
                      .pipe(takeUntil(this.destroy$))
                      .subscribe({
                        next: (res: any) => {
                          const first = Array.isArray(res?.items)
                            ? res.items[0]
                            : Array.isArray(res)
                              ? res[0]
                              : res;

                          let value: any = null;
                          const patchKey = (uicFields.patchPropertyName ?? '').trim().toLowerCase();

                          if (first) {
                            if (patchKey) {
                              const matchedKey = Object.keys(first).find(
                                k => k.toLowerCase() === patchKey
                              );
                              value = matchedKey ? first[matchedKey] : (first.value ?? first.key ?? null);
                            } else {
                              value = first.value ?? first.key ?? first;
                            }
                          }

                          newFormGroup.get(uicFields.dbFeildName)?.patchValue(value);
                          uicFields.isLoading = false;
                        },
                        error: () => {
                          uicFields.isLoading = false;
                        }
                      });
                  }
                });
              }
            }
          });
      } else if (f.uiInputType?.toLowerCase() === 'toggle' && f.dependentOn && f.dependentOn != '') {
        newFormGroup.get(f.dependentOn.toLowerCase())?.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            const localPrice = newFormGroup.get(f.dependentOn?.toLowerCase()!)?.value;
            console.log(localPrice);
          });
      } else if (f.uiInputType?.toLowerCase() === 'select' && f.searchable !== 1) {
        newFormGroup.get(f.dbFeildName)?.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe((value) => {
            console.log('Field : ', f.dbFeildName);
            console.log('value : ', value);
            console.log('defaultValue : ', f.defaultValue);
          });
      }
    });

    // Setup JS Executor listeners for all jsexecutor fields in this form instance
    clonedUIC.forEach(field => {
      if (field.dataFetchType?.toLowerCase() === 'jsexecutor' && field.dataFetchFrom) {
        this.setupJSExecutorListener(field, formInstance);
      }
    });

    // ✅ NEW: Setup dbquery triggers for this form instance
    this.setupDbQueryTriggersForInstance(clonedUIC, newFormGroup, formInstance);

    // Add to instances array
    this.formInstances.push(formInstance);
  }









}